import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Plus, Edit2, Trash2, X, AlertCircle, Search, Wrench, CheckCircle, FileText, Printer, Clock, Calendar, Download, Save, Send, BarChart3, CheckSquare, Package, Film, Eye, Upload, MapPin, RotateCcw } from 'lucide-react';
import API, { getCM, getCMById, createCM, updateCM, deleteCM, getVehicles, getInventoriSpareparts, getJasaLayanan, updateCMProgress, getKaryawan, selesaikanCM, verifikasiCM, getCMLogs, approveCM, generateTagihanFromCM, getTagihan, getPublicSettings } from '../api';
import { useAuth } from '../AuthContext';
import MultiSelectFilter from '../components/MultiSelectFilter';

// ====== CONSTANTS ======
const TABS = [
  { key: 'submission', label: 'Pengajuan CM', icon: FileText, color: '#fb6340' },
  { key: 'approved', label: 'CM Disetujui', icon: Clock, color: '#11cdef' },
  { key: 'rejected', label: 'CM Ditolak', icon: X, color: '#f5365c' },
  { key: 'completed', label: 'CM Selesai', icon: CheckCircle, color: '#2dce89' },
  { key: 'remainder', label: 'Remainder AC', icon: Clock, color: '#f59e0b' },
];

const JENIS_PEKERJAAN = ['Pemeriksaan', 'Perbaikan', 'Penggantian Sparepart/Komponen'];

// ====== Helper: Konversi UTC ke WIB (Asia/Jakarta) ======
const toWIBDate = (d) => {
  if (!d) return '-';
  const date = new Date(d);
  const formatter = new Intl.DateTimeFormat('id-ID', {
    timeZone: 'Asia/Jakarta',
    year: 'numeric', month: '2-digit', day: '2-digit'
  });
  const parts = formatter.formatToParts(date);
  const year = parts.find(p => p.type === 'year')?.value || '';
  const month = parts.find(p => p.type === 'month')?.value || '';
  const day = parts.find(p => p.type === 'day')?.value || '';
  return `${year}-${month}-${day}`;
};

// ====== Helper: Waktu sekarang dalam WIB (Asia/Jakarta) ======
const getWIBDateTime = () => {
  const now = new Date();
  const f = new Intl.DateTimeFormat('en', {
    timeZone: 'Asia/Jakarta',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
  const parts = f.formatToParts(now);
  const get = (t) => parts.find(p => p.type === t)?.value || '00';
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
};

// ====== Helper: Hitung lama menunggu approval ======

// ====== Helper: Kompresi foto ke base64 (max 800px, quality 70%) ======
const compressImage = (file, maxWidth = 800, quality = 0.7) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        // Scale down if wider than maxWidth
        if (width > maxWidth) {
          height = Math.round(height * maxWidth / width);
          width = maxWidth;
        }
        // Also scale down if taller (landscape orientation)
        if (height > maxWidth) {
          width = Math.round(width * maxWidth / height);
          height = maxWidth;
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        // Compress as JPEG with quality
        const dataUrl = canvas.toDataURL('image/jpeg', quality);
        resolve(dataUrl);
      };
      img.onerror = () => reject(new Error('Gagal memuat gambar'));
      img.src = e.target.result;
    };
    reader.onerror = () => reject(new Error('Gagal membaca file'));
    reader.readAsDataURL(file);
  });
};

const STATUS_OPTIONS = ['Diproses', 'Menunggu Sparepart', 'Sedang Dikerjakan', 'Selesai'];

// Deteksi apakah URL berisi video (data:video base64 dari mobile-web, atau file .mp4 dll)
const isVideoURL = (u) => u && (u.startsWith('data:video') || /\.(mp4|webm|mov|m4v|avi)(\?|#|$)/i.test(u));

const WORKFLOW_STATUS = {
  'Pengajuan': { color: '#fb6340', bg: '#fff5ee', label: 'Pengajuan', icon: FileText },
  'Menunggu Approval': { color: '#f5a623', bg: '#fff8e1', label: 'Menunggu Approval', icon: Clock },
  'Diproses': { color: '#11cdef', bg: '#eef9fd', label: 'Diproses', icon: Wrench },
  'Menunggu Sparepart': { color: '#8965e0', bg: '#f3edff', label: 'Menunggu Sparepart', icon: Package },
  'Sedang Dikerjakan': { color: '#5e72e4', bg: '#e8ecff', label: 'Sedang Dikerjakan', icon: Wrench },
  'Selesai': { color: '#2dce89', bg: '#d1fae5', label: 'Selesai', icon: CheckCircle },
  'Ditolak': { color: '#f5365c', bg: '#fff5f5', label: 'Ditolak', icon: X },
};

const statusConfig = {};
Object.entries(WORKFLOW_STATUS).forEach(([key, val]) => { statusConfig[key] = { color: val.color, bg: val.bg }; });

// ====== Pagination Component ======
function Pagination({ page, totalPages, total, onPageChange }) {
  if (totalPages <= 1 && total === 0) return null;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '10px 16px', marginTop: 12,
      background: '#fff', borderRadius: 8,
      border: '1px solid var(--gray-100)',
      fontSize: '0.82rem',
    }}>
      <span style={{ color: 'var(--gray-400)' }}>
        {total > 0 ? `Menampilkan halaman ${page} dari ${totalPages} (${total} total)` : `${total} data`}
      </span>
      <div style={{ display: 'flex', gap: 6 }}>
        <button
          onClick={() => onPageChange(p => Math.max(1, p - 1))}
          disabled={page <= 1}
          style={{
            padding: '5px 12px', borderRadius: 6,
            border: '1px solid var(--gray-200)',
            background: page <= 1 ? '#f9fafb' : '#fff',
            color: page <= 1 ? '#d1d5db' : '#374151',
            fontWeight: 600, fontSize: '0.78rem',
            cursor: page <= 1 ? 'not-allowed' : 'pointer',
            transition: 'all 0.15s',
          }}
        >
          ← Prev
        </button>
        <button
          onClick={() => onPageChange(p => Math.min(totalPages, p + 1))}
          disabled={page >= totalPages}
          style={{
            padding: '5px 12px', borderRadius: 6,
            border: '1px solid var(--gray-200)',
            background: page >= totalPages ? '#f9fafb' : '#fff',
            color: page >= totalPages ? '#d1d5db' : '#374151',
            fontWeight: 600, fontSize: '0.78rem',
            cursor: page >= totalPages ? 'not-allowed' : 'pointer',
            transition: 'all 0.15s',
          }}
        >
          Next →
        </button>
      </div>
    </div>
  );
}

const emptyForm = {
  vehicle_id: '', no_body: '', no_polisi: '', nama_klien: '', ketua_koperasi: '',
  rute: '', lokasi_pengerjaan: '',
  nama_teknisi: '', teknisi_handle: '',  jenis_pengerjaan: '', pengawas_pramudi: '', no_hp_pramudi: '',
  jenis_pekerjaan: '', jenis_pekerjaan_list: [], detail_pekerjaan: '',
  waktu_pemeriksaan: '', odometer: '', hasil_pemeriksaan: '',
  disetujui_oleh: '', tanda_tangan_pengawas: '',
  sparepart_digunakan: [], jasa_digunakan: [],
  foto_url: '', video_url: '', foto_urls: [], video_urls: [],
  waktu_penyelesaian: '',
  tanda_tangan_teknisi: '',
  verified_signature: '',
  nominal_tagihan: 0,
  status: '',
  jenis_kendaraan: '',
};

// ====== Section Header ======
function SectionHeader({ icon, title, subtitle, color = '#fb6340' }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, borderBottom: `2px solid ${color}`, paddingBottom: 8, marginBottom: 16, marginTop: 20 }}>
      <div style={{ width: 32, height: 32, borderRadius: 8, background: color, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
        {icon}
      </div>
      <div>
        <h4 style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--gray-700)', margin: 0 }}>{title}</h4>
        {subtitle && <small style={{ color: 'var(--gray-400)', fontSize: '0.75rem' }}>{subtitle}</small>}
      </div>
    </div>
  );
}

// ====== Status Badge ======
function StatusBadge({ status, size = 'sm' }) {
  const cfg = WORKFLOW_STATUS[status] || WORKFLOW_STATUS['Pengajuan'];
  const fontSize = size === 'sm' ? '0.7rem' : '0.78rem';
  const padding = size === 'sm' ? '3px 10px' : '5px 14px';
  return (
    <span className="badge" style={{ background: cfg.bg, color: cfg.color, fontSize, fontWeight: 600, padding, borderRadius: 20 }}>
      {status}
    </span>
  );
}

// ====== MultiFileUpload Component ======
function MultiFileUpload({ label, fileType, urls, uploading, onUpload, onRemove }) {
  const inputRef = React.useRef(null);
  const isFoto = fileType === 'foto';
  const urlList = urls || [];

  const handleFileSelect = (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    onUpload(files);
    e.target.value = '';
  };

  return (
    <div className="form-group">
      <label>{label} {urlList.length > 0 && <span style={{ fontSize: '0.7rem', color: '#2dce89', fontWeight: 600 }}>({urlList.length} file)</span>}</label>
      <div style={{
        border: '2px dashed var(--gray-200)',
        borderRadius: 8, padding: 12,
        background: urlList.length > 0 ? '#f0fdf4' : '#fafafa',
        transition: 'all 0.2s',
      }}>
        {/* Thumbnail Gallery */}
        {urlList.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
            {urlList.map((url, idx) => (
              <div key={idx} style={{ position: 'relative', width: isFoto && isVideoURL(url) ? 110 : 90, height: 70 }}>
                {isFoto && isVideoURL(url) ? (
                  <video src={url} controls muted playsInline preload="metadata"
                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6, border: '1px solid var(--gray-200)', background: '#1a1a2e' }}
                  />
                ) : isFoto ? (
                  <img src={url} alt={`Foto ${idx+1}`}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6, border: '1px solid var(--gray-200)' }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <div style={{
                    width: '100%', height: '100%', borderRadius: 6,
                    background: '#1a1a2e', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: '1px solid var(--gray-200)',
                  }}>
                    <Film size={18} color="#fff" />
                  </div>
                )}
                <button type="button" onClick={() => onRemove(idx)}
                  style={{
                    position: 'absolute', top: -4, right: -4,
                    width: 18, height: 18, borderRadius: '50%',
                    background: '#f5365c', color: '#fff', border: 'none',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.6rem', fontWeight: 700,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  }}>
                  <X size={10} />
                </button>
                <a href={url} target="_blank" rel="noopener noreferrer"
                  style={{
                    position: 'absolute', bottom: 0, left: 0, right: 0,
                    fontSize: '0.55rem', color: '#fff', textAlign: 'center',
                    background: 'rgba(0,0,0,0.5)', padding: '1px 4px',
                    borderRadius: '0 0 6px 6px', textDecoration: 'none',
                  }}>
                  {url.split('/').pop().substring(0, 15)}
                </a>
              </div>
            ))}
          </div>
        )}

        {/* Upload button */}
        <label style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          gap: 6, padding: urlList.length > 0 ? '8px' : '16px 8px', cursor: 'pointer',
          color: 'var(--gray-400)', transition: 'all 0.15s',
          border: urlList.length > 0 ? '1px dashed var(--gray-200)' : 'none',
          borderRadius: 6,
        }}
          onMouseEnter={e => e.currentTarget.style.color = '#2dce89'}
          onMouseLeave={e => e.currentTarget.style.color = 'var(--gray-400)'}>
          {uploading ? (
            <div style={{ textAlign: 'center' }}>
              <div className="spinner" style={{
                width: 24, height: 24, border: '2px solid var(--gray-200)',
                borderTopColor: '#2dce89', borderRadius: '50%',
                animation: 'spin 0.8s linear infinite', margin: '0 auto 6px',
              }} />
              <span style={{ fontSize: '0.75rem' }}>Uploading {urlList.length + 1}...</span>
            </div>
          ) : (
            <>
              <Upload size={20} />
              <span style={{ fontSize: '0.78rem' }}>Tambah {isFoto ? 'Foto' : 'Video'}</span>
              <span style={{ fontSize: '0.65rem' }}>Klik untuk pilih (bisa multi-file)</span>
            </>
          )}
          <input ref={inputRef} type="file" hidden
            accept={isFoto ? 'image/*' : 'video/*'}
            multiple
            onChange={handleFileSelect}
          />
        </label>
      </div>
    </div>
  );
}

// ====== Signature Pad Component ======
function SignaturePad({ value, onChange, label, height = 100 }) {
  const canvasRef = React.useRef(null);
  const [isDrawing, setIsDrawing] = React.useState(false);
  const [hasContent, setHasContent] = React.useState(false);
  const [activeMode, setActiveMode] = React.useState('draw'); // 'draw' or 'upload'
  const fileInputRef = React.useRef(null);

  // Initialize canvas on mount or when value changes externally
  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#1a1a2e';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    // If there's an existing value (base64), load it
    if (value && value.startsWith('data:')) {
      const img = new Image();
      img.onload = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        setHasContent(true);
      };
      img.src = value;
    } else {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setHasContent(false);
    }
  }, [value]);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: (clientX - rect.left) * (canvas.width / rect.width),
      y: (clientY - rect.top) * (canvas.height / rect.height),
    };
  };

  const startDraw = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setIsDrawing(true);
    setHasContent(true);
  };

  const draw = (e) => {
    e.preventDefault();
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const stopDraw = (e) => {
    e.preventDefault();
    if (isDrawing) {
      setIsDrawing(false);
      // Auto-save after drawing
      const canvas = canvasRef.current;
      if (canvas && hasContent) {
        const dataUrl = canvas.toDataURL('image/png');
        onChange(dataUrl);
      }
    }
  };

  const handleClear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasContent(false);
    onChange('');
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target?.result;
      if (typeof dataUrl === 'string') {
        // Draw image onto canvas
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        const img = new Image();
        img.onload = () => {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          // Fit image to canvas
          const scale = Math.min(canvas.width / img.width, canvas.height / img.height, 1);
          const x = (canvas.width - img.width * scale) / 2;
          const y = (canvas.height - img.height * scale) / 2;
          ctx.drawImage(img, x, y, img.width * scale, img.height * scale);
          setHasContent(true);
          onChange(canvas.toDataURL('image/png'));
        };
        img.src = dataUrl;
      }
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  return (
    <div className="form-group">
      <label>{label}</label>
      <div style={{
        border: '2px dashed #d1d5db',
        borderRadius: 8,
        background: value ? '#f0fdf4' : '#fafafa',
        padding: 8,
        transition: 'all 0.2s',
      }}>
        {/* Mode toggle: Draw / Upload */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
          <button type="button" onClick={() => setActiveMode('draw')}
            style={{
              flex: 1, padding: '4px 8px', fontSize: '0.72rem', fontWeight: 600,
              border: activeMode === 'draw' ? '2px solid #2dce89' : '1px solid #d1d5db',
              borderRadius: 4, cursor: 'pointer',
              background: activeMode === 'draw' ? '#e8f8f0' : '#fff',
              color: activeMode === 'draw' ? '#065f46' : '#6b7280',
            }}>
            ✏️ Tanda Tangan
          </button>
          <button type="button" onClick={() => setActiveMode('upload')}
            style={{
              flex: 1, padding: '4px 8px', fontSize: '0.72rem', fontWeight: 600,
              border: activeMode === 'upload' ? '2px solid #2dce89' : '1px solid #d1d5db',
              borderRadius: 4, cursor: 'pointer',
              background: activeMode === 'upload' ? '#e8f8f0' : '#fff',
              color: activeMode === 'upload' ? '#065f46' : '#6b7280',
            }}>
            📷 Upload Gambar
          </button>
        </div>

        {/* Canvas for drawing */}
        {activeMode === 'draw' && (
          <div style={{ position: 'relative' }}>
            <canvas
              ref={canvasRef}
              width={400}
              height={height}
              onMouseDown={startDraw}
              onMouseMove={draw}
              onMouseUp={stopDraw}
              onMouseLeave={stopDraw}
              onTouchStart={startDraw}
              onTouchMove={draw}
              onTouchEnd={stopDraw}
              style={{
                width: '100%',
                height,
                border: '1px solid #e5e7eb',
                borderRadius: 4,
                cursor: 'crosshair',
                background: '#fff',
                touchAction: 'none',
              }}
            />
            {!hasContent && !value && (
              <div style={{
                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#d1d5db', fontSize: '0.78rem', pointerEvents: 'none',
              }}>
                Tanda tangan di sini
              </div>
            )}
          </div>
        )}

        {/* Upload button */}
        {activeMode === 'upload' && (
          <div style={{ textAlign: 'center', padding: '12px 0' }}>
            <label style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '8px 16px', cursor: 'pointer',
              color: '#6b7280', fontSize: '0.82rem',
              border: '1px dashed #d1d5db', borderRadius: 6,
              transition: 'all 0.15s',
            }}
              onMouseEnter={e => e.currentTarget.style.color = '#2dce89'}
              onMouseLeave={e => e.currentTarget.style.color = '#6b7280'}>
              <Upload size={16} />
              <span>Pilih file gambar (PNG/JPG)</span>
              <input ref={fileInputRef} type="file" hidden accept="image/png,image/jpeg" onChange={handleFileUpload} />
            </label>
          </div>
        )}

        {/* Action buttons */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginTop: 6 }}>
          {hasContent || value ? (
            <button type="button" onClick={handleClear}
              style={{
                padding: '4px 10px', fontSize: '0.7rem', fontWeight: 600,
                color: '#f5365c', background: '#fff5f5',
                border: '1px solid #fecaca', borderRadius: 4, cursor: 'pointer',
              }}>
              <X size={12} style={{ marginRight: 3 }} />
              Hapus
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// ====== Verifikasi Section ======
function VerifikasiSection({ form, onChange }) {
  return (
    <div style={{ background: '#fffbeb', borderRadius: 8, border: '1px solid #fde68a', padding: 16, marginTop: 16 }}>
      <SectionHeader icon={<CheckSquare size={16} />} title="Verifikasi & Tanda Tangan" subtitle="Pengesahan laporan pekerjaan" color="#d97706" />
      <div style={{ fontSize: '0.82rem', color: '#92400e', fontStyle: 'italic', marginBottom: 12, padding: '10px 14px', background: '#fef3c7', borderRadius: 6, borderLeft: '3px solid #d97706' }}>
        Dengan ini dinyatakan bahwa laporan pekerjaan telah diperiksa dan diverifikasi kebenarannya. Berdasarkan hasil pemeriksaan tersebut, ditetapkan nilai tagihan sebagaimana tercantum di bawah ini.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div className="form-group"><label>Diperiksa & Diterima Oleh</label><input type="text" value={form.verified_by || ''} onChange={e => onChange('verified_by', e.target.value)} placeholder="Nama pemeriksa" /></div>
        <div className="form-group"><label>Jabatan</label><input type="text" value={form.verified_jabatan || ''} onChange={e => onChange('verified_jabatan', e.target.value)} placeholder="Jabatan" /></div>
        <div className="form-group"><label>Nominal Tagihan (Rp)</label><input type="number" value={form.nominal_tagihan || 0} onChange={e => onChange('nominal_tagihan', Number(e.target.value))} placeholder="0" min="0" /></div>
        <div className="form-group"><label>Tanggal</label><input type="date" value={form.verified_date || new Date().toISOString().slice(0, 10)} onChange={e => onChange('verified_date', e.target.value)} /></div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginTop: 12 }}>
        <SignaturePad value={form.verified_signature || ''} onChange={v => onChange('verified_signature', v)} label="TTD Diperiksa & Diterima" height={90} />
        <SignaturePad value={form.tanda_tangan_pengawas || ''} onChange={v => onChange('tanda_tangan_pengawas', v)} label="Tanda Tangan Pengawas" height={90} />
        <SignaturePad value={form.tanda_tangan_teknisi || ''} onChange={v => onChange('tanda_tangan_teknisi', v)} label="Tanda Tangan Teknisi" height={90} />
      </div>
    </div>
  );
}

// ====== Detail View Modal ======
function DetailModal({ item, onClose, onVerifikasi }) {
  const [verifikasiForm, setVerifikasiForm] = useState({
    verified_by: item.verified_by || '',
    verified_jabatan: item.verified_jabatan || '',
    nominal_tagihan: item.nominal_tagihan || 0,
    verified_date: item.verified_date?.slice(0, 10) || new Date().toISOString().slice(0, 10),
    verified_signature: item.verified_signature || '',
    tanda_tangan_pengawas: item.tanda_tangan_pengawas || '',
    tanda_tangan_teknisi: item.tanda_tangan_teknisi || '',
  });
  const [saving, setSaving] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  // Default TTD & stempel perusahaan (untuk cetak) — diambil dari app_settings
  const [printDefaults, setPrintDefaults] = useState({ ttd: '', stempel: '' });

  // Fetch public settings (TTD & stempel default) for printing
  useEffect(() => {
    getPublicSettings()
      .then(res => setPrintDefaults({
        ttd: res.data?.ttd_direktur || '',
        stempel: res.data?.stempel_perusahaan || '',
      }))
      .catch(() => setPrintDefaults({ ttd: '', stempel: '' }));
  }, []);

  // Fetch activity logs for this CM
  useEffect(() => {
    if (!item?.id) return;
    setLogsLoading(true);
    getCMLogs(item.id)
      .then(res => setLogs(Array.isArray(res.data) ? res.data : []))
      .catch(() => setLogs([]))
      .finally(() => setLogsLoading(false));
  }, [item?.id]);

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('id-ID', { year: 'numeric', month: 'long', day: 'numeric' }) : '-';
  const fmtDateTime = (d) => d ? new Date(d).toLocaleString('id-ID') : '-';

  const totalSparepart = (item.sparepart_digunakan || []).reduce((s, sp) => s + (sp.jumlah || 0) * (sp.harga || 0), 0);
  const totalJasa = (item.jasa_digunakan || []).reduce((s, js) => s + (js.jumlah || 0) * (js.harga || 0), 0);
  const grandTotal = totalSparepart + totalJasa;

  const handleVerifikasiSave = async () => {
    setSaving(true);
    try {
      await verifikasiCM(item.id, {
        ...verifikasiForm,
        nominal_tagihan: Number(verifikasiForm.nominal_tagihan) || 0,
      });
      onVerifikasi();
      onClose();
    } catch (err) {
      alert('Gagal verifikasi: ' + (err.response?.data?.error || err.message));
    } finally { setSaving(false); }
  };

  const handleCetak = () => {
    const printWindow = window.open('', '_blank', 'width=900,height=700');
    if (!printWindow) { alert('Popup diblokir. Izinkan popup untuk mencetak.'); return; }

    const sekarang = new Date();
    const fmtDate2 = (d) => d ? new Date(d).toLocaleDateString('id-ID', { year: 'numeric', month: 'long', day: 'numeric' }) : '-';
    const fmtDateTime2 = (d) => d ? new Date(d).toLocaleString('id-ID') : '-';
    const renderSignature = (val, fallback = '______________') => {
      if (!val) return fallback;
      if (typeof val === 'string' && val.startsWith('data:')) {
        return `<img src="${val}" style="max-height:35px;max-width:120px;vertical-align:middle;border:1px solid #ddd;border-radius:3px;padding:1px;background:#fff;" alt="TTD" />`;
      }
      return val;
    };

    const totalSP = (item.sparepart_digunakan || []).reduce((s, sp) => s + (sp.jumlah || 0) * (sp.harga || 0), 0);
    const totalJS = (item.jasa_digunakan || []).reduce((s, js) => s + (js.jumlah || 0) * (js.harga || 0), 0);
    const grandTotal = totalSP + totalJS;

    printWindow.document.write(`<!DOCTYPE html><html><head><title>Cetak CM - ${item.cm_id}</title>
<style>
  @page { margin: 1cm 0.8cm; }
  body { font-family: 'Times New Roman', Times, serif; font-size: 10pt; color: #000; line-height: 1.25; margin: 0; padding: 0; }
  .no-print { text-align: center; margin-bottom: 6px; }
  .no-print button { padding: 6px 16px; background: #1a1a2e; color: #fff; border: none; border-radius: 4px; cursor: pointer; font-size: 11px; font-family: sans-serif; }
  .header-table { width: 100%; border-collapse: collapse; margin-bottom: 2px; }
  .header-table td { vertical-align: top; padding: 0; }
  .header-left { text-align: left; width: 45%; }
  .header-left .logo-img { max-width: 120px; max-height: 38px; margin-bottom: 3px; }
  .header-left .title-print { font-size: 10pt; font-weight: 700; margin: 1px 0; text-transform: uppercase; }
  .header-left .subtitle-print { font-size: 7pt; color: #555; font-style: italic; margin: 0; }
  .header-left .cm-id-print { font-size: 8pt; font-weight: 600; margin: 2px 0 0; }
  .header-right { text-align: right; width: 55%; font-size: 6.5pt; line-height: 1.2; }
  .header-right .company-name { font-size: 7.5pt; font-weight: 700; margin: 0 0 2px; }
  .header-right .address-line { margin: 0; }
  .header-right .address-label { font-weight: 600; }
  .header-separator { border: none; border-top: 2px double #000; margin: 3px 0 4px; }
  .section-box { border: 1.5px solid #1a1a2e; margin-bottom: 4px; }
  .section-header { background: #1a1a2e; color: #fff; padding: 2px 8px; font-weight: 700; font-size: 8pt; letter-spacing: 0.5px; }
  .section-body { padding: 3px 8px; }
  table.info-grid { width: 100%; border-collapse: collapse; font-size: 8.5pt; }
  table.info-grid td { padding: 1px 4px; vertical-align: top; }
  table.info-grid td.label { font-weight: 700; width: 90px; }
  table.info-grid td.val { border-bottom: 1px solid #ccc; padding: 0 3px 1px; }
  .radio-item { font-size: 8.5pt; padding: 0; }
  .radio-checked { font-weight: 700; color: #1a1a2e; }
  .divider-dashed { border-top: 1px dashed #999; margin: 4px 0; }
  table.items { width: 100%; border-collapse: collapse; font-size: 8pt; margin-top: 3px; }
  table.items th { border: 1px solid #000; padding: 2px 4px; text-align: center; font-weight: 700; background: #f0f0f0; font-size: 7.5pt; }
  table.items td { border: 1px solid #000; padding: 1px 3px; vertical-align: middle; }
  table.items td.price { text-align: right; }
  table.items td.center { text-align: center; }
  /* Foto dokumentasi — 2 kolom × 2 baris per halaman (1 foto ≈ 1/4 halaman).
     Section-nya dibiarkan boleh pecah antar-halaman supaya foto bisa besar. */
  .foto-print-section { page-break-inside: auto; }
  .foto-print-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; align-items: start; }
  .foto-print-item { text-align: center; page-break-inside: avoid; break-inside: avoid; }
  .foto-print-img { width: 100%; max-width: 100%; max-height: 118mm; object-fit: contain; border: 1px solid #ccc; border-radius: 3px; background: #fafafa; }
  .foto-print-caption { font-size: 8pt; color: #555; margin-top: 2px; }
  .statement { font-style: italic; font-size: 8pt; padding: 3px 8px; background: #fef9ef; border: 1px solid #fde68a; border-left: 3px solid #d97706; margin: 3px 0; }
  .ttd-section { display: flex; justify-content: space-between; margin-top: 8px; padding: 0 8px; }
  .ttd-box { text-align: center; width: 160px; }
  .ttd-box .ttd-line { margin-top: 20px; border-top: 1px solid #000; padding-top: 2px; font-size: 8pt; font-weight: 600; }
  .footer { text-align: center; font-size: 6.5pt; color: #888; margin-top: 6px; border-top: 1px solid #ccc; padding-top: 2px; }
  table.log-table { width: 100%; border-collapse: collapse; margin-top: 3px; font-size: 7.5pt; }
  table.log-table th { border: 1px solid #000; padding: 1px 3px; text-align: center; font-weight: 700; background: #f0f0f0; font-size: 7pt; }
  table.log-table td { border: 1px solid #000; padding: 1px 3px; vertical-align: middle; }
  .tagihan-box { text-align: right; font-size: 9pt; font-weight: 700; padding: 2px 6px; background: #f0fdf4; border: 1px solid #b8e6d0; margin: 3px 0; }
  @media print { .no-print { display: none !important; } }
</style></head><body>
<div class="no-print"><button onclick="window.print()">🖨 Cetak / Simpan PDF</button></div>

<table class="header-table">
  <tr>
    <td class="header-left">
      <img src="${window.location.origin}/logo-synexa.png" alt="Synexa Logo" class="logo-img" onerror="this.style.display='none'" />
      <div class="title-print">LAPORAN LAYANAN ARMADA</div>
      <div class="subtitle-print">Fleet Service Report</div>
      <div class="cm-id-print">No.CM : ${item.cm_id || '#' + item.id}</div>
    </td>
    <td class="header-right">
      <div class="company-name">PT. Synexa Global Tekno</div>
      <div class="address-line"><span class="address-label">Tangerang :</span> Ruko Silktown Avenue Blok RK/5A-05</div>
      <div class="address-line">Jl. Boulevard Silk Town, Kel. Pondok Jagung Timur, Kec. Serpong Utara,</div>
      <div class="address-line">Kota Tangerang Selatan - Banten 15320</div>
      <div class="address-line"><span class="address-label">Jakarta :</span> Office 8 SCBD Lt.17, Jl. Senopati - Jakarta Selatan</div>
      <div class="address-line">Website : www.synexa.co.id &nbsp;|&nbsp; Email : info@synexa.co.id</div>
    </td>
  </tr>
</table>
<hr class="header-separator" />

<!-- SECTION 1: INFORMASI UMUM -->
<div class="section-box">
  <div class="section-header">INFORMASI UMUM</div>
  <div class="section-body">
    <table class="info-grid">
      <tr><td class="label">Nama Klien</td><td class="val">${item.nama_klien || '______________________'}</td>
          <td class="label" style="width:110px">Ketua Koperasi</td><td class="val">${item.ketua_koperasi || '______________________'}</td></tr>
      <tr><td class="label">Rute</td><td class="val">${item.rute || '______________'}</td>
          <td class="label">Nama Teknisi</td><td class="val">${item.nama_teknisi || '______________'}</td></tr>
      <tr><td class="label">No. Body</td><td class="val">${item.no_body || '______________'}</td>
          <td class="label">Detail Pekerjaan</td><td class="val">${item.detail_pekerjaan || '______________'}</td></tr>
      <tr><td class="label">No. Polisi</td><td class="val">${item.no_polisi || '______________'}</td>
          <td class="label">Lokasi Pengerjaan</td><td class="val">${item.lokasi_pengerjaan || '______________'}</td></tr>
      <tr><td class="label">Pengawas / Pramudi</td><td class="val">${item.pengawas_pramudi || '______________'}</td>
          <td class="label">No. HP Pramudi</td><td class="val">${item.no_hp_pramudi || '______________'}</td></tr>
      <tr><td class="label">Odometer</td><td class="val">${item.odometer ? item.odometer + ' km' : '______________'}</td>
          <td class="label"></td><td class="val"></td></tr>
        
    </table>
  </div>
</div>

<!-- SECTION 2: INFORMASI LEMBAR PEMERIKSAAN -->
<div class="section-box">
  <div class="section-header">INFORMASI LEMBAR PEMERIKSAAN</div>
  <div class="section-body">
    <div style="font-size:8.5pt;margin-bottom:3px;">
      <strong>Detail / Uraian Pekerjaan:</strong><br/>
      <span style="font-size:8pt;">${(item.detail_pekerjaan || '-').replace(/\n/g, '<br/>')}</span>
    </div>
    <div class="divider-dashed"></div>
    <table style="width:100%;font-size:9.5pt;">
      <tr>
        <td style="width:50%;vertical-align:top;">
          <strong>Waktu Pemeriksaan:</strong><br/>
          <span>${fmtDateTime2(item.waktu_pemeriksaan)}</span>
        </td>
        <td style="width:50%;vertical-align:top;">
          <strong>Hasil Pemeriksaan Disetujui Oleh:</strong><br/>
          <span>${item.hasil_pemeriksaan || '-'}</span><br/>
          <div style="margin-top:4px;">
            <strong>TTD Pengawas / Pramudi:</strong><br/>
            ${renderSignature(item.tanda_tangan_pengawas)}
          </div>
        </td>
      </tr>
    </table>
  </div>
</div>

<!-- SECTION 3: INFORMASI LEMBAR HASIL PEKERJAAN -->
<div class="section-box">
  <div class="section-header">INFORMASI LEMBAR HASIL PEKERJAAN</div>
  <div class="section-body">
    ${(item.sparepart_digunakan || []).length + (item.jasa_digunakan || []).length > 0 ? `
    <table class="items">
      <thead><tr><th style="width:25px">No</th><th>Nama Barang / Jasa</th><th style="width:50px">Jumlah</th><th style="width:70px">Harga</th><th style="width:80px">Subtotal</th><th>Hasil Pengerjaan</th></tr></thead>
      <tbody>
        ${(item.sparepart_digunakan || []).map((sp, i) => `
          <tr><td class="center">${i+1}</td><td>${sp.nama || '-'}</td><td class="center">${sp.jumlah || 0}</td><td class="price">Rp ${(sp.harga || 0).toLocaleString('id-ID')}</td><td class="price">Rp ${((sp.jumlah||0)*(sp.harga||0)).toLocaleString('id-ID')}</td><td>${sp.hasil_pekerjaan || '-'}</td></tr>
        `).join('')}
        ${(item.jasa_digunakan || []).map((js, i) => {
          const idx = (item.sparepart_digunakan || []).length + i + 1;
          return `<tr><td class="center">${idx}</td><td>${js.nama || '-'} <em>(Jasa)</em></td><td class="center">${js.jumlah || 0}</td><td class="price">Rp ${(js.harga || 0).toLocaleString('id-ID')}</td><td class="price">Rp ${((js.jumlah||0)*(js.harga||0)).toLocaleString('id-ID')}</td><td>${js.hasil_pekerjaan || '-'}</td></tr>`;
        }).join('')}
      </tbody>
    </table>
    ` : '<div style="text-align:center;font-style:italic;color:#999;padding:8px;font-size:9pt">Belum ada data sparepart / jasa</div>'}
  </div>
</div>

<!-- SECTION FOTO: DOKUMENTASI FOTO -->
${(() => {
  const baseUrl = window.location.origin;
  const fotoList = item.foto_urls || (item.foto_url ? [item.foto_url] : []);
  const resolveUrl = (u) => {
    if (!u) return '';
    if (u.startsWith('data:')) return u;
    if (u.startsWith('http://') || u.startsWith('https://')) return u;
    return baseUrl + u;
  };
  return fotoList.length > 0 ? `
<div class="section-box foto-print-section">
  <div class="section-header">DOKUMENTASI FOTO</div>
  <div class="section-body">
    <div class="foto-print-grid">
      ${fotoList.map((url, i) => {
        const fullUrl = resolveUrl(url);
        const isVid = isVideoURL(fullUrl);
        return `
        <div class="foto-print-item">
          ${isVid
            ? `<video src="${fullUrl}" controls style="width:100%;max-height:118mm;border:1px solid #ccc;border-radius:3px;background:#1a1a2e"></video>`
            : `<img src="${fullUrl}" alt="Foto ${i+1}" class="foto-print-img" onerror="this.parentElement.innerHTML='<em style=font-size:6pt;color:#999>Foto ${i+1} (gagal dimuat)</em>'" />`}
          <div class="foto-print-caption">${isVid ? 'Video ' + (i+1) : 'Foto ' + (i+1)}</div>
        </div>`;
      }).join('')}
    </div>
  </div>
</div>
` : '';
})()}

<!-- SECTION 4: PENYELESAIAN & TTD -->
<div class="section-box">
  <div class="section-header">PENYELESAIAN PEKERJAAN</div>
  <div class="section-body">
    <table style="width:100%;font-size:9.5pt;">
      <tr>
        <td style="width:35%;vertical-align:top;">
          <strong>Waktu Pengerjaan:</strong><br/>
          <span style="font-size:8.5pt;">${fmtDateTime2(item.waktu_penyelesaian) || '______________'}</span>
        </td>
        <td style="width:65%;vertical-align:top;">
          <div style="font-style:italic;font-size:8pt;padding:2px 6px;background:#f9f9f9;border:1px solid #ddd;margin-bottom:3px;">
            Dengan ini dinyatakan bahwa pekerjaan telah selesai dikerjakan dan diterima dengan baik sesuai checklist catatan di atas.
          </div>
          <table style="width:100%;font-size:8pt;">
            <tr>
              <td style="width:50%;padding:1px 3px;"><strong>TTD Pengawas / Pramudi:</strong><br/>${renderSignature(item.tanda_tangan_pengawas)}</td>
              <td style="width:50%;padding:1px 3px;"><strong>TTD Teknisi:</strong><br/>${renderSignature(item.tanda_tangan_teknisi)}</td>
            </tr>
          </table>
        </td>
      </tr>
    </table>
  </div>
</div>

<!-- SECTION 5: VERIFIKASI & TAGIHAN -->
<div class="section-box">
  <div class="section-header">VERIFIKASI & PENETAPAN TAGIHAN</div>
  <div class="section-body">
    <div class="statement">
      <strong>Pernyataan Pemeriksaan Hasil Pekerjaan:</strong><br/>
      "Dengan ini dinyatakan bahwa laporan pekerjaan telah diperiksa dan diverifikasi kebenarannya berdasarkan hasil pemeriksaan tersebut, ditetapkan nilai tagihan sebagaimana tercantum di bawah ini."
    </div>
    <table style="width:100%;font-size:9.5pt;">
      <tr>
        <td style="width:35%;vertical-align:top;">
          <strong>Nominal Tagihan:</strong><br/>
          <span style="font-size:11pt;font-weight:700;color:#1aae6f;">Rp ${(item.nominal_tagihan || grandTotal).toLocaleString('id-ID')}</span>
        </td>
        <td style="width:65%;vertical-align:top;text-align:right;">
          <div style="display:inline-block;text-align:center;">
          <div style="font-weight:700;font-size:9pt;margin-bottom:6px;">Diperiksa &amp; Diterima oleh,</div>
          <div style="min-height:72px;min-width:200px;margin:2px auto;">${(() => {
            const v = item.verified_signature || printDefaults.ttd;
            const stempel = printDefaults.stempel;
            const ttdHtml = v
              ? (v.startsWith('data:')
                  ? `<img src="${v}" style="max-height:58px;max-width:200px;vertical-align:middle;position:relative;z-index:2;filter:brightness(0) contrast(1.2) drop-shadow(0.6px 0 0 #000) drop-shadow(-0.6px 0 0 #000) drop-shadow(0 0.6px 0 #000) drop-shadow(0 -0.6px 0 #000);" alt="TTD" />`
                  : `<span style="position:relative;z-index:2;">${v}</span>`)
              : '<span style="color:#aaa;font-size:7pt;font-style:italic;position:relative;z-index:2;">upload TTD</span>';
            // Stempel tampil sebagai watermark di BELAKANG TTD (overlay di area tanda tangan)
            if (!stempel) return `<span style="display:inline-block;vertical-align:middle;">${ttdHtml}</span>`;
            const stempelHtml = `<img src="${stempel}" style="position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);max-height:76px;max-width:170px;opacity:0.45;z-index:1;" alt="Stempel" />`;
            return `<span style="position:relative;display:inline-block;min-width:200px;min-height:72px;vertical-align:middle;">${stempelHtml}${ttdHtml}</span>`;
          })()}</div>
          <div style="font-size:9pt;font-weight:600;margin-top:2px;">Achmad Saudi</div>
          <div style="font-size:8pt;">Direktur Operasional</div>
          </div>
        </td>
      </tr>
    </table>
  </div>
</div>

      <!-- RIWAYAT AKTIVITAS (sembunyikan dari cetak) -->

<div class="footer">Dicetak dari sistem LayananSynexa FMS — ${fmtDate2(sekarang)} — Dokumen resmi</div>
</body></html>`);
    printWindow.document.close();
    setTimeout(() => { try { printWindow.print(); } catch(e) {} }, 500);
  };

  if (!item) return null;

  return (
    <div className="modal-overlay" onClick={onClose} style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1000 }}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 900, width: '95%', maxHeight: '90vh', overflow: 'auto', borderRadius: 12 }}>
        <div className="modal-header" style={{ background: 'linear-gradient(135deg, #2dce89, #1aae6f)', color: '#fff', borderRadius: '12px 12px 0 0', padding: '14px 20px', position: 'sticky', top: 0, zIndex: 10 }}>
          <div>
            <h3 style={{ color: '#fff', margin: 0, fontSize: '1rem' }}>Detail CM — {item.cm_id || `#${item.id}`}</h3>
            <small style={{ opacity: 0.85 }}>{item.no_polisi} — <StatusBadge status={item.status} /></small>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn-icon" onClick={handleCetak} style={{ color: '#fff', background: 'rgba(255,255,255,0.15)' }} title="Cetak">
              <Printer size={18} />
            </button>
            <button className="btn-icon" onClick={onClose} style={{ color: '#fff' }}><X size={20} /></button>
          </div>
        </div>

        <div className="modal-body" style={{ padding: '16px 24px', background: '#fff' }}>
          {/* INFORMASI UMUM */}
          <SectionHeader icon={<FileText size={16} />} title="Informasi Umum" color="#fb6340" subtitle="Data kendaraan dan petugas" />
          <table className="identity" style={{ width: '100%', fontSize: '0.85rem', marginBottom: 12 }}>
            <tbody>
              <tr><td style={{ fontWeight: 600, width: 130 }}>Nama Klien</td><td>: {item.nama_klien || '-'}</td><td style={{ fontWeight: 600, width: 130 }}>Ketua Koperasi</td><td>: {item.ketua_koperasi || '-'}</td></tr>
              <tr><td style={{ fontWeight: 600 }}>Rute</td><td>: {item.rute || '-'}</td><td style={{ fontWeight: 600 }}>Nama Teknisi</td><td>: {item.nama_teknisi || '-'}</td></tr>
              <tr><td style={{ fontWeight: 600 }}>Lokasi</td><td>: {item.lokasi_pengerjaan || '-'}</td><td style={{ fontWeight: 600 }}>Detail Pekerjaan</td><td>: {item.detail_pekerjaan || '-'}</td></tr>
              <tr><td style={{ fontWeight: 600 }}>Jenis Pengerjaan</td><td>: {item.jenis_pengerjaan || '-'}</td><td style={{ fontWeight: 600 }}>No. Polisi</td><td>: <strong>{item.no_polisi || '-'}</strong></td></tr>
              <tr><td style={{ fontWeight: 600 }}>No. Body</td><td>: {item.no_body || '-'}</td><td style={{ fontWeight: 600 }}>Pengawas/Pramudi</td><td>: {item.pengawas_pramudi || '-'}</td></tr>
              <tr><td style={{ fontWeight: 600 }}>Pengawas/Pramudi</td><td>: {item.pengawas_pramudi || '-'}</td><td style={{ fontWeight: 600 }}>No. HP Pramudi</td><td>: {item.no_hp_pramudi || '-'}</td></tr>
              <tr><td style={{ fontWeight: 600 }}>Odometer</td><td>: {item.odometer ? `${item.odometer} km` : '-'}</td><td style={{ fontWeight: 600 }}></td><td></td></tr>
            </tbody>
          </table>

          {/* HASIL PEMERIKSAAN */}
          <SectionHeader icon={<CheckCircle size={16} />} title="Hasil Pemeriksaan" color="#11cdef" subtitle="Detail pemeriksaan dan persetujuan" />
          <div style={{ background: '#f8f9fa', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: '0.85rem' }}>
              <div><strong>Detail Pekerjaan:</strong><br/>{item.detail_pekerjaan || '-'}</div>
              <div><strong>Waktu Pemeriksaan:</strong><br/>{fmtDateTime(item.waktu_pemeriksaan)}</div>
              <div><strong>Hasil Pemeriksaan:</strong><br/>{item.hasil_pemeriksaan || '-'}</div>
              <div><strong>Disetujui Oleh:</strong><br/>{item.disetujui_oleh || '-'}</div>
              <div style={{ gridColumn: '1 / -1' }}><strong>Keterangan:</strong><br/>{item.catatan || '-'}</div>
            </div>
          </div>

          {/* HASIL PEKERJAAN */}
          <SectionHeader icon={<Wrench size={16} />} title="Hasil Pekerjaan" color="#8965e0" subtitle="Sparepart dan jasa yang digunakan" />
          {item.sparepart_digunakan?.length > 0 && (
            <table className="data-table" style={{ marginBottom: 12, fontSize: '0.82rem' }}>
              <thead><tr><th>Sparepart</th><th style={{ width: 60 }}>Jumlah</th><th style={{ width: 100 }}>Harga</th><th style={{ width: 100 }}>Subtotal</th><th>Hasil</th></tr></thead>
              <tbody>
                {item.sparepart_digunakan.map((sp, i) => (
                  <tr key={i}><td>{sp.nama}</td><td style={{ textAlign: 'center' }}>{sp.jumlah}</td><td style={{ textAlign: 'right' }}>Rp {(sp.harga || 0).toLocaleString('id-ID')}</td><td style={{ textAlign: 'right' }}>Rp {((sp.jumlah||0)*(sp.harga||0)).toLocaleString('id-ID')}</td><td>{sp.hasil_pekerjaan || '-'}</td></tr>
                ))}
              </tbody>
            </table>
          )}
          {item.jasa_digunakan?.length > 0 && (
            <table className="data-table" style={{ marginBottom: 12, fontSize: '0.82rem' }}>
              <thead><tr><th>Jasa Layanan</th><th style={{ width: 60 }}>Jml</th><th style={{ width: 100 }}>Harga</th><th style={{ width: 100 }}>Subtotal</th></tr></thead>
              <tbody>
                {item.jasa_digunakan.map((js, i) => (
                  <tr key={i}><td>{js.nama}</td><td style={{ textAlign: 'center' }}>{js.jumlah}</td><td style={{ textAlign: 'right' }}>Rp {(js.harga || 0).toLocaleString('id-ID')}</td><td style={{ textAlign: 'right' }}>Rp {((js.jumlah||0)*(js.harga||0)).toLocaleString('id-ID')}</td></tr>
                ))}
              </tbody>
            </table>
          )}
          <div style={{ textAlign: 'right', fontSize: '1rem', fontWeight: 700, color: '#1aae6f', padding: '8px 16px', background: '#e8f8f0', borderRadius: 8, marginBottom: 16 }}>
            Total: Rp {(item.nominal_tagihan || grandTotal).toLocaleString('id-ID')}
          </div>

          {/* DOKUMENTASI FOTO */}
          <SectionHeader icon={<Film size={16} />} title="Dokumentasi Foto & Video" color="#f59e0b" subtitle="Dokumentasi pekerjaan" />
          <div style={{ marginBottom: 20 }}>
            {(() => {
              const origin = window.location.origin;
              const resolveUrl = (u) => u && u.startsWith('data:') ? u : origin + u;
              const fotoList = item.foto_urls || (item.foto_url ? [item.foto_url] : []);
              const videoList = item.video_urls || (item.video_url ? [item.video_url] : []);
              // Beberapa foto dari mobile-web tersimpan sebagai video (data:video/mp4) di kolom
              // foto_urls — tampilkan sebagai <video> agar tetap bisa dilihat di frontend.
              return (
                <>
                  {fotoList.length > 0 && (
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--gray-500)', marginBottom: 8 }}>📷 Foto ({fotoList.length})</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {fotoList.map((url, idx) => {
                          const fullUrl = resolveUrl(url);
                          const isVid = isVideoURL(fullUrl);
                          return isVid ? (
                            <div key={idx} style={{ width: 140, height: 90, borderRadius: 6, overflow: 'hidden', border: '1px solid var(--gray-200)', cursor: 'pointer', background: '#1a1a2e', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}
                              onClick={() => window.open(fullUrl, '_blank')}>
                              <video src={fullUrl} controls muted playsInline preload="metadata" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, fontSize: '0.55rem', color: '#fff', textAlign: 'center', background: 'rgba(0,0,0,0.6)', padding: '1px 4px' }}>
                                Video {idx+1}
                              </div>
                            </div>
                          ) : (
                            <div key={idx} style={{ width: 100, height: 80, borderRadius: 6, overflow: 'hidden', border: '1px solid var(--gray-200)', cursor: 'pointer' }}
                              onClick={() => window.open(fullUrl, '_blank')}>
                              <img src={fullUrl} alt={`Foto ${idx+1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                onError={(e) => { e.target.style.display = 'none'; }} />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {videoList.length > 0 && (
                    <div>
                      <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--gray-500)', marginBottom: 8 }}>🎬 Video ({videoList.length})</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {videoList.map((url, idx) => {
                          const fullUrl = resolveUrl(url);
                          return (
                            <div key={idx} style={{ width: 120, height: 80, borderRadius: 6, overflow: 'hidden', border: '1px solid var(--gray-200)', background: '#1a1a2e', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', position: 'relative' }}
                              onClick={() => window.open(fullUrl, '_blank')}>
                              <Film size={24} color="#fff" />
                              <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, fontSize: '0.55rem', color: '#fff', textAlign: 'center', background: 'rgba(0,0,0,0.6)', padding: '1px 4px' }}>
                                Video {idx+1}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {fotoList.length === 0 && videoList.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '16px 0', color: 'var(--gray-400)', fontSize: '0.82rem', fontStyle: 'italic' }}>
                      Belum ada dokumentasi foto/video
                    </div>
                  )}
                </>
              );
            })()}
          </div>

          {/* VERIFIKASI */}
          {item.status === 'Selesai' && !item.verified_by && (
            <VerifikasiSection form={verifikasiForm} onChange={(key, val) => setVerifikasiForm(f => ({ ...f, [key]: val }))} />
          )}

          {item.verified_by && (
            <div style={{ background: '#e8f8f0', borderRadius: 8, padding: '12px 16px', border: '1px solid #b8e6d0' }}>
              <SectionHeader icon={<CheckSquare size={16} />} title="Verifikasi" subtitle="Telah diverifikasi" color="#2dce89" />
              <div style={{ fontSize: '0.85rem', color: '#065f46', marginTop: 8 }}>
                Diverifikasi oleh: <strong>{item.verified_by}</strong> ({item.verified_jabatan})<br/>
                Tanggal: {fmtDate(item.verified_date)}<br/>
                Nominal Tagihan: <strong>Rp {(item.nominal_tagihan || 0).toLocaleString('id-ID')}</strong>
              </div>
            </div>
          )}

          {/* RIWAYAT AKTIVITAS */}
          <div style={{ marginTop: 16 }}>
            <SectionHeader icon={<Clock size={16} />} title="Riwayat Aktivitas" subtitle="Log perubahan status CM" color="#5e72e4" />
            {logsLoading ? (
              <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--gray-400)', fontSize: '0.82rem' }}>Memuat riwayat...</div>
            ) : logs.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--gray-400)', fontSize: '0.82rem' }}>Belum ada riwayat aktivitas</div>
            ) : (
              <div style={{ maxHeight: 250, overflowY: 'auto', borderRadius: 8, border: '1px solid var(--gray-100)' }}>
                {logs.map((log, idx) => {
                  const isLatest = idx === 0;
                  return (
                    <div key={log.id || idx} style={{
                      display: 'flex', gap: 12,
                      padding: '10px 14px',
                      borderBottom: idx < logs.length - 1 ? '1px solid var(--gray-50)' : 'none',
                      background: isLatest ? '#eef2ff' : '#fff',
                      fontSize: '0.82rem',
                      transition: 'background 0.2s',
                    }}>
                      <div style={{
                        width: 28, height: 28, borderRadius: '50%',
                        background: isLatest ? '#5e72e4' : '#e9ecef',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                        <Clock size={12} color={isLatest ? '#fff' : '#8898aa'} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: isLatest ? 600 : 400, color: 'var(--gray-700)', lineHeight: 1.5 }}>
                          {log.description || '-'}
                        </div>
                        <div style={{ display: 'flex', gap: 8, fontSize: '0.7rem', color: 'var(--gray-400)', marginTop: 4 }}>
                          <span>{log.created_at || '-'}</span>
                          {log.username && <span>— oleh <strong>{log.username}</strong></span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer" style={{ padding: '12px 24px', borderTop: '1px solid var(--gray-100)', display: 'flex', justifyContent: 'flex-end', gap: 10, background: '#fafafa', borderRadius: '0 0 12px 12px' }}>
          {item.status === 'Selesai' && !item.verified_by && (
            <button className="btn btn-primary" onClick={handleVerifikasiSave} disabled={saving} style={{ background: 'linear-gradient(135deg, #d97706, #b45309)', border: 'none' }}>
              {saving ? 'Menyimpan...' : 'Simpan Verifikasi'}
            </button>
          )}
          <button className="btn btn-secondary" onClick={onClose}>Tutup</button>
        </div>
      </div>
    </div>
  );
}

// ====== Searchable Vehicle Dropdown ======
function VehicleSearch({ vehicles, selectedId, onSelect }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const selectedVehicle = vehicles.find(v => v.id === Number(selectedId));

  const filtered = vehicles.filter(v => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (v.body_no || '').toLowerCase().includes(q) ||
           (v.route_code || v.trayek_no || '').toLowerCase().includes(q);
  }).slice(0, 300);

  // Close on click outside
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <input
        type="text"
        value={open ? query : (selectedVehicle ? `${selectedVehicle.body_no || '-'} — ${selectedVehicle.route_code || selectedVehicle.trayek_no || '-'}` : '')}
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => { setQuery(''); setOpen(true); }}
        placeholder="Ketik No Body / Rute..."
        style={{ width: '100%', padding: '6px 8px', border: '1px solid #ccc', borderRadius: 0, fontSize: '0.82rem', background: '#fff', boxSizing: 'border-box', fontWeight: selectedId ? 600 : 400, cursor: 'text' }}
      />
      {selectedId && !open && (
        <div style={{ position: 'absolute', right: 8, top: 6, fontSize: '0.65rem', color: '#2dce89', fontWeight: 600, pointerEvents: 'none' }}>
          ✓ terpilih
        </div>
      )}
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
          maxHeight: 200, overflowY: 'auto',
          background: '#fff', border: '1px solid #ccc', borderTop: 'none',
          borderRadius: '0 0 4px 4px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        }}>
          {filtered.length === 0 ? (
            <div style={{ padding: '8px 10px', color: '#999', fontSize: '0.78rem', fontStyle: 'italic' }}>
              Tidak ditemukan
            </div>
          ) : filtered.map(v => (
            <div key={v.id}
              onClick={() => { onSelect(v); setOpen(false); setQuery(''); }}
              style={{
                padding: '7px 10px', cursor: 'pointer', fontSize: '0.8rem',
                borderBottom: '1px solid #f0f0f0',
                background: Number(selectedId) === v.id ? '#fff5ee' : '#fff',
                transition: 'background 0.1s',
              }}
              onMouseEnter={e => e.target.style.background = '#f5f5f5'}
              onMouseLeave={e => e.target.style.background = Number(selectedId) === v.id ? '#fff5ee' : '#fff'}
            >
              <strong style={{ fontSize: '0.82rem' }}>{v.body_no || '-'}</strong>
              <span style={{ color: '#666', marginLeft: 8, fontSize: '0.78rem' }}>{v.route_code || v.trayek_no || '-'}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ====== Searchable Item Dropdown (Sparepart / Jasa Layanan) ======
function SearchableItemSelect({ items, selectedName, onSelect, placeholder, searchFields, displayFormat }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const selectedItem = items.find(i => i.name === selectedName || i.nama === selectedName);

  const filtered = items.filter(item => {
    if (!query) return true;
    const q = query.toLowerCase();
    const fields = searchFields || ['name', 'nama', 'kode'];
    return fields.some(f => (item[f] || '').toLowerCase().includes(q));
  }).slice(0, 200);

  // Close on click outside
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const getDisplayValue = () => {
    if (!selectedItem) return '';
    if (displayFormat) return displayFormat(selectedItem);
    return selectedItem.name || selectedItem.nama || '-';
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <input
        type="text"
        value={open ? query : getDisplayValue()}
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => { setQuery(''); setOpen(true); }}
        placeholder={placeholder || 'Ketik untuk mencari...'}
        style={{ width: '100%', padding: '3px 4px', border: 'none', borderRadius: 0, fontSize: '0.75rem', background: '#fff', boxSizing: 'border-box', fontWeight: selectedItem ? 600 : 400, cursor: 'text' }}
      />
      {selectedName && !open && (
        <div style={{ position: 'absolute', right: 4, top: 3, fontSize: '0.6rem', color: '#2dce89', fontWeight: 600, pointerEvents: 'none' }}>
          ✓
        </div>
      )}
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
          maxHeight: 180, overflowY: 'auto',
          background: '#fff', border: '1px solid #ccc', borderTop: 'none',
          borderRadius: '0 0 4px 4px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        }}>
          {filtered.length === 0 ? (
            <div style={{ padding: '8px 10px', color: '#999', fontSize: '0.72rem', fontStyle: 'italic' }}>
              Tidak ditemukan
            </div>
          ) : filtered.map(item => (
            <div key={item.id}
              onClick={() => { onSelect(item); setOpen(false); setQuery(''); }}
              style={{
                padding: '5px 8px', cursor: 'pointer', fontSize: '0.75rem',
                borderBottom: '1px solid #f0f0f0',
                background: (selectedName && (item.name === selectedName || item.nama === selectedName)) ? '#fff5ee' : '#fff',
                transition: 'background 0.1s',
              }}
              onMouseEnter={e => e.target.style.background = '#f5f5f5'}
              onMouseLeave={e => e.target.style.background = (selectedName && (item.name === selectedName || item.nama === selectedName)) ? '#fff5ee' : '#fff'}
            >
              {displayFormat ? displayFormat(item) : <strong>{item.name || item.nama || '-'}</strong>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ====== Tab 1: Pengajuan CM Form (Redesigned) ======
function FormPengajuan({ onSaved, vehicles, spareparts, jasaLayanan, karyawanList, editingId, editingData, onCancelEdit, pmInitData }) {
  const { user } = useAuth();
  const [form, setForm] = useState({ ...emptyForm, waktu_pemeriksaan: getWIBDateTime(), waktu_penyelesaian: getWIBDateTime(), nama_teknisi: user?.full_name || user?.username || '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState({ foto: false, video: false });

  // BUG FIX: use ref to prevent form reset when vehicles load/refresh.
  // Previously, `vehicles` in the dependency array caused this useEffect to
  // re-fire when vehicles finished loading, resetting ALL user edits
  // (sparepart/jasa additions/modifications) back to the original editingData.
  const editInitIdRef = useRef(null);

  // Populate form when editing — ONLY when editingId changes (new CM opened)
  useEffect(() => {
    if (editingData && editingId && editingId !== editInitIdRef.current) {
      editInitIdRef.current = editingId;
      const fmtWaktu = editingData.waktu_pemeriksaan ? editingData.waktu_pemeriksaan.slice(0, 16) : getWIBDateTime();
      const fmtSelesai = editingData.waktu_penyelesaian ? editingData.waktu_penyelesaian.slice(0, 16) : getWIBDateTime();
      // Find vehicle to get jenis_kendaraan
      const vehicle = vehicles.find(v => v.id === editingData.vehicle_id);
      // Parse comma-separated jenis_pekerjaan into array
      const pekerjaanStr = editingData.jenis_pekerjaan || '';
      const pekerjaanList = pekerjaanStr ? pekerjaanStr.split(',').map(s => s.trim()).filter(Boolean) : [];
      // Build foto_urls/video_urls from existing data
      const fotoUrls = editingData.foto_urls || (editingData.foto_url ? [editingData.foto_url] : []);
      const videoUrls = editingData.video_urls || (editingData.video_url ? [editingData.video_url] : []);
      setForm({
        vehicle_id: editingData.vehicle_id || '',
        no_body: editingData.no_body || '',
        no_polisi: editingData.no_polisi || '',
        nama_klien: editingData.nama_klien || '',
        ketua_koperasi: editingData.ketua_koperasi || '',
        rute: editingData.rute || '',
        lokasi_pengerjaan: editingData.lokasi_pengerjaan || '',
        nama_teknisi: editingData.nama_teknisi || user?.full_name || '',
        teknisi_handle: editingData.teknisi_handle || '',
        jenis_pengerjaan: editingData.jenis_pengerjaan || '',
        pengawas_pramudi: editingData.pengawas_pramudi || '',
        no_hp_pramudi: editingData.no_hp_pramudi || '',
        jenis_pekerjaan: editingData.jenis_pekerjaan || '',
        jenis_pekerjaan_list: pekerjaanList,
        detail_pekerjaan: editingData.detail_pekerjaan || '',
        waktu_pemeriksaan: fmtWaktu,
        odometer: editingData.odometer || '',
        hasil_pemeriksaan: editingData.hasil_pemeriksaan || '',
        disetujui_oleh: editingData.disetujui_oleh || '',
        tanda_tangan_pengawas: editingData.tanda_tangan_pengawas || '',
        sparepart_digunakan: editingData.sparepart_digunakan || [],
        jasa_digunakan: editingData.jasa_digunakan || [],
        foto_url: editingData.foto_url || '',
        video_url: editingData.video_url || '',
        foto_urls: fotoUrls,
        video_urls: videoUrls,
        waktu_penyelesaian: fmtSelesai,
        tanda_tangan_teknisi: editingData.tanda_tangan_teknisi || '',
        verified_signature: editingData.verified_signature || '',
        nominal_tagihan: editingData.nominal_tagihan || 0,
        status: editingData.status || '',
        jenis_kendaraan: vehicle?.jenis_ac || '',
      });
    }
  }, [editingId, editingData, vehicles]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset ref when editing is cancelled or completed
  useEffect(() => {
    if (!editingId) { editInitIdRef.current = null; }
  }, [editingId]);

  // Update jenis_kendaraan when vehicles finish loading (without resetting other fields)
  useEffect(() => {
    if (editingData && editingId && vehicles.length > 0) {
      const vehicle = vehicles.find(v => v.id === editingData.vehicle_id);
      if (vehicle) {
        setForm(f => ({ ...f, jenis_kendaraan: vehicle.jenis_ac || '' }));
      }
    }
  }, [vehicles, editingData, editingId]);

  const teknisiList = karyawanList.filter(k => {
    const j = (k.jabatan || '').toLowerCase();
    const b = (k.bagian_name || '').toLowerCase();
    return j.includes('teknisi') || b.includes('teknisi');
  });
  const finalTeknisiList = teknisiList.length > 0 ? teknisiList : karyawanList;

  const selectVehicle = (v) => {
    if (v) {
      setForm(f => ({
        ...f,
        vehicle_id: v.id,
        no_body: v.body_no || '',
        no_polisi: v.plate_no || '',
        nama_klien: v.operator || '',
        ketua_koperasi: v.ketua_koperasi || '',
        rute: v.route_code || v.trayek_no || '',
        lokasi_pengerjaan: v.pengendapan || '',
        jenis_kendaraan: v.jenis_ac || '',
      }));
    } else {
      setForm(f => ({
        ...f,
        vehicle_id: '', no_body: '', no_polisi: '',
        nama_klien: '', rute: '', lokasi_pengerjaan: '',
        jenis_kendaraan: '',
      }));
    }
  };

  const addSparepartRow = () => setForm(f => ({ ...f, sparepart_digunakan: [...f.sparepart_digunakan, { nama: '', jumlah: 1, harga: 0, hasil_pekerjaan: '', status: 'Selesai' }] }));
  const updateSparepartRow = (idx, field, value) => { const items = [...form.sparepart_digunakan]; items[idx] = { ...items[idx], [field]: ['jumlah', 'harga'].includes(field) ? Number(value) : value }; setForm({ ...form, sparepart_digunakan: items }); };
  const removeSparepartRow = (idx) => setForm(f => ({ ...f, sparepart_digunakan: f.sparepart_digunakan.filter((_, i) => i !== idx) }));
  const selectSparepartName = (idx, spId) => { const selected = spareparts.find(s => s.id === Number(spId)); if (selected) { const items = [...form.sparepart_digunakan]; items[idx] = { nama: selected.name || '', jumlah: 1, harga: Number(selected.harga_jual || 0), hasil_pekerjaan: '', status: 'Selesai' }; setForm({ ...form, sparepart_digunakan: items }); } };

  const addJasaRow = () => setForm(f => ({ ...f, jasa_digunakan: [...f.jasa_digunakan, { nama: '', jumlah: 1, harga: 0, hasil_pekerjaan: '', status: '' }] }));
  const updateJasaRow = (idx, field, value) => { const items = [...form.jasa_digunakan]; items[idx] = { ...items[idx], [field]: ['jumlah', 'harga'].includes(field) ? Number(value) : value }; setForm({ ...form, jasa_digunakan: items }); };
  const removeJasaRow = (idx) => setForm(f => ({ ...f, jasa_digunakan: f.jasa_digunakan.filter((_, i) => i !== idx) }));
  const selectJasaFromMaster = (idx, jasaId) => { const selected = jasaLayanan.find(j => j.id === Number(jasaId)); if (selected) { const items = [...form.jasa_digunakan]; items[idx] = { nama: selected.nama, jumlah: 1, harga: Number(selected.harga_jual || 0), hasil_pekerjaan: '', status: '' }; setForm({ ...form, jasa_digunakan: items }); } };

  const handleMultiUpload = async (files, fileType) => {
    if (!files || files.length === 0) return;
    setUploading(prev => ({ ...prev, [fileType]: true }));
    try {
      // === FOTO: kompres → base64 langsung, simpan ke DB ===
      if (fileType === 'foto') {
        const base64Promises = Array.from(files).map(f => compressImage(f, 800, 0.7));
        const base64Urls = await Promise.all(base64Promises);
        setForm(prev => {
          const existing = prev.foto_urls || [];
          return { ...prev, foto_urls: [...existing, ...base64Urls] };
        });
      } else {
        // === VIDEO: tetap upload file ke server ===
        const formData = new FormData();
        Array.from(files).forEach(f => formData.append('files', f));
        formData.append('type', fileType);
        const res = await API.post('/cm/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        const newUrls = res.data.urls || (res.data.url ? [res.data.url] : []);
        setForm(prev => {
          const key = 'video_urls';
          const existing = prev[key] || [];
          return { ...prev, [key]: [...existing, ...newUrls] };
        });
      }
    } catch (err) {
      setError('Gagal upload ' + fileType + ': ' + (err.response?.data?.error || err.message));
    } finally { setUploading(prev => ({ ...prev, [fileType]: false })); }
  };

  const handleRemoveFile = (idx, fileType) => {
    setForm(prev => {
      const key = fileType === 'foto' ? 'foto_urls' : 'video_urls';
      const updated = (prev[key] || []).filter((_, i) => i !== idx);
      return { ...prev, [key]: updated };
    });
  };

  // Populate form from PM data (when coming from PM menu)
  useEffect(() => {
    if (pmInitData) {
      // Find vehicle data to auto-fill nama_klien, rute, jenis_kendaraan, lokasi
      let vehicle = null;
      if (pmInitData.vehicle_id) {
        vehicle = vehicles.find(v => v.id === Number(pmInitData.vehicle_id));
      }
      if (!vehicle && pmInitData.no_body) {
        vehicle = vehicles.find(v => v.body_no === pmInitData.no_body);
      }
      if (!vehicle && pmInitData.no_polisi) {
        vehicle = vehicles.find(v => v.plate_no === pmInitData.no_polisi);
      }
      setForm(f => ({
        ...f,
        pm_id: pmInitData.pm_id || '',
        vehicle_id: pmInitData.vehicle_id || '',
        no_body: pmInitData.no_body || (vehicle ? vehicle.body_no || '' : ''),
        no_polisi: pmInitData.no_polisi || (vehicle ? vehicle.plate_no || '' : ''),
        nama_teknisi: pmInitData.nama_teknisi || '',
        pengawas_pramudi: pmInitData.pengawas_pramudi || '',
        no_hp_pramudi: pmInitData.no_hp_pramudi || '',
        detail_pekerjaan: pmInitData.detail_pekerjaan || '',
        // Auto-fill from vehicle data
        nama_klien: vehicle ? (vehicle.operator || vehicle.owner_name || '') : '',
        ketua_koperasi: vehicle ? (vehicle.ketua_koperasi || '') : '',
        rute: vehicle ? (vehicle.route_code || vehicle.trayek_no || '') : '',
        jenis_kendaraan: vehicle ? (vehicle.jenis_ac || '') : '',
        lokasi_pengerjaan: vehicle ? (vehicle.pengendapan || '') : '',
      }));
    }
  }, [pmInitData, vehicles]);

  // Calculate totals
  const totalSparepart = form.sparepart_digunakan.reduce((s, sp) => s + (sp.jumlah || 0) * (sp.harga || 0), 0);
  const totalJasa = form.jasa_digunakan.reduce((s, js) => s + (js.jumlah || 0) * (js.harga || 0), 0);
  const grandTotal = totalSparepart + totalJasa;

  const handleSave = async (status) => {
    setError('');
    if (!form.nama_teknisi) { setError('Nama teknisi harus diisi'); return; }
    const pekerjaanList = Array.isArray(form.jenis_pekerjaan_list) ? form.jenis_pekerjaan_list : [];
    if (pekerjaanList.length === 0) { setError('Pilih minimal satu jenis pekerjaan'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        vehicle_id: form.vehicle_id ? Number(form.vehicle_id) : null,
        pm_id: form.pm_id ? Number(form.pm_id) : null,
        odometer: form.odometer ? Number(form.odometer) : null,
        waktu_pemeriksaan: form.waktu_pemeriksaan || null,
        waktu_penyelesaian: form.waktu_penyelesaian || null,
        // Selalu pakai grandTotal agar nominal_tagihan ikut berubah saat sparepart/jasa diubah
        nominal_tagihan: grandTotal,
        // Saat edit, pertahankan status CM yang sudah ada (mis. 'Selesai') agar tidak berubah
        status: editingId ? (form.status || status) : status,
        foto_urls: form.foto_urls || [],
        video_urls: form.video_urls || [],
        foto_url: (form.foto_urls && form.foto_urls.length > 0) ? form.foto_urls[0] : (form.foto_url || ''),
        video_url: (form.video_urls && form.video_urls.length > 0) ? form.video_urls[0] : (form.video_url || ''),
      };
      if (editingId) {
        await updateCM(editingId, payload);
      } else {
        await createCM(payload);
      }
      setForm({ ...emptyForm, waktu_pemeriksaan: getWIBDateTime(), waktu_penyelesaian: getWIBDateTime(), nama_teknisi: user?.full_name || '' });
      if (onCancelEdit) onCancelEdit();
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Gagal menyimpan');
    } finally { setSaving(false); }
  };

  // ====== SECTION STYLES ======
  const sectionStyle = {
    border: '2px solid #1a1a2e',
    borderRadius: 0,
    marginBottom: 20,
    background: '#fff',
  };
  const sectionHeaderStyle = {
    background: '#1a1a2e',
    color: '#fff',
    padding: '6px 16px',
    fontWeight: 700,
    fontSize: '0.82rem',
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
  };
  const sectionBodyStyle = { padding: 16 };
  const fieldLabelStyle = {
    fontSize: '0.75rem',
    fontWeight: 700,
    color: '#1a1a2e',
    marginBottom: 2,
    whiteSpace: 'nowrap',
  };
  const fieldInputStyle = {
    width: '100%',
    padding: '6px 8px',
    border: '1px solid #ccc',
    borderRadius: 0,
    fontSize: '0.82rem',
    background: '#fff',
    boxSizing: 'border-box',
  };
  const dividerH = { borderBottom: '1px dashed #999', margin: '12px 0' };

  return (
    <div>
      <div style={{ background: 'linear-gradient(135deg, #fff5ee, #fff)', borderRadius: 12, padding: 20, border: '1px solid #fed7aa', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: 'linear-gradient(135deg, #fb6340, #f5365c)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
            <FileText size={22} />
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#c0392b' }}>{editingId ? 'Edit Pengajuan CM' : 'Tambah Pengajuan CM Baru'}</h3>
            <p style={{ margin: '2px 0 0', fontSize: '0.78rem', color: 'var(--gray-500)' }}>Layanan Armada — Fleet Service Report</p>
          </div>
        </div>
      </div>

      {error && (
        <div className="form-error" style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#fff5f5', border: '1px solid #fecaca', borderRadius: 8, color: '#c0392b', fontSize: '0.82rem' }}>
          <AlertCircle size={14} /><span>{error}</span>
        </div>
      )}

      {/* ============================================
          SECTION 1: INFORMASI UMUM
          ============================================ */}
      <div style={sectionStyle}>
        <div style={sectionHeaderStyle}>
          INFORMASI UMUM
        </div>
        <div style={sectionBodyStyle}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px 16px' }}>
            {/* Row 1: No Body (search dropdown) | Teknisi 1 | Teknisi 2 */}
            <div>
              <div style={fieldLabelStyle}>Cari No. Body / Kendaraan <span style={{ color: '#e74c3c' }}>*</span></div>
              <VehicleSearch
                vehicles={vehicles}
                selectedId={form.vehicle_id}
                onSelect={(v) => selectVehicle(v)}
              />
            </div>
            <div>
              <div style={fieldLabelStyle}>Nama Teknisi 1 <span style={{ color: '#e74c3c' }}>*</span></div>
              <select value={form.nama_teknisi} onChange={e => setForm({ ...form, nama_teknisi: e.target.value })}
                style={{ ...fieldInputStyle, background: '#fff' }}>
                <option value="">-- Pilih Teknisi --</option>
                {finalTeknisiList.map(k => <option key={k.id} value={k.nama}>{k.nama}</option>)}
              </select>
            </div>
            <div>
              <div style={fieldLabelStyle}>Nama Teknisi 2</div>
              <select value={form.teknisi_handle} onChange={e => setForm({ ...form, teknisi_handle: e.target.value })}
                style={{ ...fieldInputStyle, background: '#fff' }}>
                <option value="">-- Pilih Teknisi 2 --</option>
                {finalTeknisiList.filter(k => k.nama !== form.nama_teknisi).map(k => <option key={k.id} value={k.nama}>{k.nama}</option>)}
              </select>
            </div>
            {/* Row 2: Nama Klien (auto) | Ketua Koperasi (auto) | Rute (auto) */}
            <div>
              <div style={fieldLabelStyle}>Nama Klien</div>
              <input type="text" value={form.nama_klien} onChange={e => setForm({ ...form, nama_klien: e.target.value })}
                placeholder="Otomatis dari pemilihan kendaraan" style={{ ...fieldInputStyle, background: '#f5f5f5' }} readOnly />
            </div>
            <div>
              <div style={fieldLabelStyle}>Ketua Koperasi</div>
              <input type="text" value={form.ketua_koperasi || ''} onChange={e => setForm({ ...form, ketua_koperasi: e.target.value })}
                placeholder="Otomatis dari kendaraan" style={{ ...fieldInputStyle, background: '#f5f5f5' }} readOnly />
            </div>
            <div>
              <div style={fieldLabelStyle}>Rute / Trayek</div>
              <input type="text" value={form.rute} onChange={e => setForm({ ...form, rute: e.target.value })}
                placeholder="Otomatis dari kendaraan" style={{ ...fieldInputStyle, background: '#f5f5f5' }} readOnly />
            </div>
            <div>
              <div style={fieldLabelStyle}>Jenis Kendaraan</div>
              <div style={{
                ...fieldInputStyle, display: 'flex', alignItems: 'center', gap: 8,
                background: '#f5f5f5', padding: '6px 8px',
              }}>
                {form.jenis_kendaraan ? (
                  <>
                    <span style={{
                      display: 'inline-block', padding: '1px 8px', borderRadius: 4,
                      fontSize: '0.75rem', fontWeight: 700,
                      background: form.jenis_kendaraan === 'AC' ? '#dbeafe' : '#fef3c7',
                      color: form.jenis_kendaraan === 'AC' ? '#1d4ed8' : '#92400e',
                    }}>{form.jenis_kendaraan}</span>
                  </>
                ) : (
                  <span style={{ color: '#aaa', fontSize: '0.75rem' }}>Otomatis dari kendaraan</span>
                )}
              </div>
            </div>
            {/* Row 3: No Polisi (auto) | Lokasi Pengerjaan (auto) | Pengawas */}
            <div>
              <div style={fieldLabelStyle}>No. Polisi</div>
              <input type="text" value={form.no_polisi} onChange={e => setForm({ ...form, no_polisi: e.target.value })}
                placeholder="Otomatis" style={{ ...fieldInputStyle, background: '#f5f5f5' }} readOnly />
            </div>
            <div>
              <div style={fieldLabelStyle}>Lokasi Pengerjaan
                {form.lokasi_pengerjaan && <span style={{ fontSize: '0.65rem', color: '#2dce89', fontWeight: 400, marginLeft: 6 }}>✓ terisi otomatis</span>}
              </div>
              <input type="text" value={form.lokasi_pengerjaan} onChange={e => setForm({ ...form, lokasi_pengerjaan: e.target.value })}
                placeholder={form.vehicle_id ? 'Ketik manual jika belum ada data pengendapan' : 'Pilih kendaraan dulu atau ketik manual'}
                style={{ ...fieldInputStyle, background: form.lokasi_pengerjaan ? '#f0fdf4' : '#fff' }} />
            </div>
            <div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={fieldLabelStyle}>Pengawas / Pramudi</div>
                  <input type="text" value={form.pengawas_pramudi} onChange={e => setForm({ ...form, pengawas_pramudi: e.target.value })}
                    placeholder="Nama pengawas" style={fieldInputStyle} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={fieldLabelStyle}>No. HP Pramudi</div>
                  <input type="text" value={form.no_hp_pramudi} onChange={e => setForm({ ...form, no_hp_pramudi: e.target.value })}
                    placeholder="Nomor HP" style={fieldInputStyle} />
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* ============================================
          SECTION 2: INFORMASI LEMBAR PEMERIKSAAN
          ============================================ */}
      <div style={sectionStyle}>
        <div style={sectionHeaderStyle}>
          INFORMASI LEMBAR PEMERIKSAAN
        </div>
        <div style={sectionBodyStyle}>
          {/* Jenis Pekerjaan (radio) | Detail Pekerjaan */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <div style={fieldLabelStyle}>Jenis Pekerjaan <span style={{ color: '#e74c3c' }}>*</span></div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {JENIS_PEKERJAAN.map(opt => {
                  const checked = Array.isArray(form.jenis_pekerjaan_list) && form.jenis_pekerjaan_list.includes(opt);
                  return (
                    <label key={opt} style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '4px 0', cursor: 'pointer', fontSize: '0.82rem',
                      fontWeight: checked ? 700 : 400,
                      color: checked ? '#1a1a2e' : '#555',
                    }}>
                      <div style={{
                        width: 16, height: 16, borderRadius: 3,
                        border: checked ? '2px solid #fb6340' : '2px solid #ccc',
                        background: checked ? '#fb6340' : 'transparent',
                        transition: 'all 0.15s',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {checked && <span style={{ color: '#fff', fontSize: '0.6rem', fontWeight: 700 }}>✓</span>}
                      </div>
                      <span>{opt}</span>
                      <input type="checkbox" value={opt}
                        checked={checked}
                        onChange={e => {
                          const list = Array.isArray(form.jenis_pekerjaan_list) ? [...form.jenis_pekerjaan_list] : [];
                          if (e.target.checked) {
                            list.push(opt);
                          } else {
                            const idx = list.indexOf(opt);
                            if (idx >= 0) list.splice(idx, 1);
                          }
                          setForm({ ...form, jenis_pekerjaan_list: list, jenis_pekerjaan: list.join(', ') });
                        }}
                        style={{ display: 'none' }} />
                    </label>
                  );
                })}
              </div>
            </div>
            <div>
              <div style={fieldLabelStyle}>Detail / Uraian Pekerjaan</div>
              <textarea rows={5} value={form.detail_pekerjaan} onChange={e => setForm({ ...form, detail_pekerjaan: e.target.value })}
                placeholder="Uraikan detail pekerjaan yang dilakukan..."
                style={{ ...fieldInputStyle, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6, minHeight: 100 }} />
            </div>
          </div>

          <div style={dividerH} />

          {/* Waktu Pemeriksaan (kiri) | TTD Pengawas (kanan) + Odometer */}
          <div style={{ display: 'grid',gridTemplateColumns: '1fr 1fr',
            gap: 16 }}>
            <div>
              <div style={fieldLabelStyle}>Waktu Pemeriksaan</div>
              <input type="datetime-local" value={form.waktu_pemeriksaan}
                onChange={e => setForm({ ...form, waktu_pemeriksaan: e.target.value })}
                style={fieldInputStyle} />
            </div>
            <div>
              <div style={fieldLabelStyle}>TTD Pengawas / Pramudi <span style={{ fontSize: '0.7rem', fontWeight: 400, color: '#999' }}>(untuk paraf tangan)</span></div>
              <SignaturePad value={form.tanda_tangan_pengawas || ''} onChange={v => setForm(f => ({ ...f, tanda_tangan_pengawas: v }))} height={90} />
              <div style={{ marginTop: 12 }}>
                <div style={fieldLabelStyle}>Odometer Armada (km)</div>
                <input type="number" value={form.odometer} onChange={e => setForm({ ...form, odometer: e.target.value })}
                  placeholder="0" min="0" style={fieldInputStyle} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ============================================
          SECTION 3: INFORMASI LEMBAR HASIL PEKERJAAN
          ============================================ */}
      <div style={sectionStyle}>
        <div style={sectionHeaderStyle}>
          INFORMASI LEMBAR HASIL PEKERJAAN
        </div>
        <div style={sectionBodyStyle}>
          {/* Sparepart Table */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <span style={{ fontWeight: 700, fontSize: '0.78rem', color: '#333' }}>Sparepart & Komponen</span>
              <button className="btn btn-secondary" style={{ fontSize: '0.72rem', padding: '3px 8px', borderRadius: 4 }}
                onClick={addSparepartRow}><Plus size={10} style={{ marginRight: 3 }} />Tambah Baris</button>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
              <thead>
                <tr style={{ background: '#f0f0f0' }}>
                  <th style={{ border: '1px solid #ccc', padding: 4, fontWeight: 700, textAlign: 'center' }}>Nama Sparepart / Komponen</th>
                  <th style={{ border: '1px solid #ccc', padding: 4, fontWeight: 700, width: 50, textAlign: 'center' }}>Jumlah</th>
                  <th style={{ border: '1px solid #ccc', padding: 4, fontWeight: 700, width: 80, textAlign: 'center' }}>Harga</th>
                  <th style={{ border: '1px solid #ccc', padding: 4, fontWeight: 700, textAlign: 'center' }}>Hasil Pengerjaan</th>
                  <th style={{ border: '1px solid #ccc', padding: 4, fontWeight: 700, width: 40, textAlign: 'center' }}></th>
                </tr>
              </thead>
              <tbody>
                {form.sparepart_digunakan.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ border: '1px solid #ccc', padding: '12px 8px', textAlign: 'center', color: '#999', fontStyle: 'italic' }}>
                      Belum ada sparepart — klik "Tambah Baris" di atas
                    </td>
                  </tr>
                ) : form.sparepart_digunakan.map((sp, idx) => (
                  <tr key={idx}>
                    <td style={{ border: '1px solid #ccc', padding: 2 }}>
                      <SearchableItemSelect
                        items={spareparts}
                        selectedName={sp.nama}
                        onSelect={(item) => selectSparepartName(idx, item.id)}
                        placeholder="Ketik nama sparepart..."
                        searchFields={['name', 'nama']}
                        displayFormat={(item) => `${item.name || item.nama || '-'} (Stok: ${item.stock ?? '-'})`}
                      />
                    </td>
                    <td style={{ border: '1px solid #ccc', padding: 2 }}>
                      <input type="number" value={sp.jumlah} onChange={e => updateSparepartRow(idx, 'jumlah', e.target.value)}
                        min="1" style={{ width: '100%', padding: '3px 2px', border: 'none', fontSize: '0.75rem', textAlign: 'center' }} />
                    </td>
                    <td style={{ border: '1px solid #ccc', padding: 2 }}>
                      <input type="number" value={sp.harga} readOnly
                        style={{ width: '100%', padding: '3px 2px', border: 'none', fontSize: '0.75rem', textAlign: 'right', background: '#f5f5f5', cursor: 'not-allowed' }} />
                    </td>
                    <td style={{ border: '1px solid #ccc', padding: 2 }}>
                      <input type="text" value={sp.hasil_pekerjaan || ''} onChange={e => updateSparepartRow(idx, 'hasil_pekerjaan', e.target.value)}
                        placeholder="Hasil" style={{ width: '100%', padding: '3px 4px', border: 'none', fontSize: '0.75rem' }} />
                    </td>
                    <td style={{ border: '1px solid #ccc', padding: 2, textAlign: 'center' }}>
                      <button className="btn-icon btn-danger" onClick={() => removeSparepartRow(idx)}
                        style={{ width: 22, height: 22, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                        <X size={9} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Jasa Layanan Table */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <span style={{ fontWeight: 700, fontSize: '0.78rem', color: '#333' }}>Jasa Layanan</span>
              <button className="btn btn-secondary" style={{ fontSize: '0.72rem', padding: '3px 8px', borderRadius: 4 }}
                onClick={addJasaRow}><Plus size={10} style={{ marginRight: 3 }} />Tambah Baris</button>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
              <thead>
                <tr style={{ background: '#f0f0f0' }}>
                  <th style={{ border: '1px solid #ccc', padding: 4, fontWeight: 700, textAlign: 'center' }}>Nama Jasa Layanan</th>
                  <th style={{ border: '1px solid #ccc', padding: 4, fontWeight: 700, width: 50, textAlign: 'center' }}>Jumlah</th>
                  <th style={{ border: '1px solid #ccc', padding: 4, fontWeight: 700, width: 80, textAlign: 'center' }}>Harga</th>
                  <th style={{ border: '1px solid #ccc', padding: 4, fontWeight: 700, textAlign: 'center' }}>Hasil Pengerjaan</th>
                  <th style={{ border: '1px solid #ccc', padding: 4, fontWeight: 700, width: 40, textAlign: 'center' }}></th>
                </tr>
              </thead>
              <tbody>
                {form.jasa_digunakan.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ border: '1px solid #ccc', padding: '12px 8px', textAlign: 'center', color: '#999', fontStyle: 'italic' }}>
                      Belum ada jasa layanan — klik "Tambah Baris" di atas
                    </td>
                  </tr>
                ) : form.jasa_digunakan.map((js, idx) => (
                  <tr key={idx}>
                    <td style={{ border: '1px solid #ccc', padding: 2 }}>
                      <SearchableItemSelect
                        items={jasaLayanan.filter(j => j.is_active !== false)}
                        selectedName={js.nama}
                        onSelect={(item) => selectJasaFromMaster(idx, item.id)}
                        placeholder="Ketik nama jasa..."
                        searchFields={['nama', 'kode']}
                        displayFormat={(item) => `${item.kode ? item.kode + ' - ' : ''}${item.nama || '-'}`}
                      />
                    </td>
                    <td style={{ border: '1px solid #ccc', padding: 2 }}>
                      <input type="number" value={js.jumlah} onChange={e => updateJasaRow(idx, 'jumlah', e.target.value)}
                        min="1" style={{ width: '100%', padding: '3px 2px', border: 'none', fontSize: '0.75rem', textAlign: 'center' }} />
                    </td>
                    <td style={{ border: '1px solid #ccc', padding: 2 }}>
                      <input type="number" value={js.harga} readOnly
                        style={{ width: '100%', padding: '3px 2px', border: 'none', fontSize: '0.75rem', textAlign: 'right', background: '#f5f5f5', cursor: 'not-allowed' }} />
                    </td>
                    <td style={{ border: '1px solid #ccc', padding: 2 }}>
                      <input type="text" value={js.hasil_pekerjaan || ''} onChange={e => updateJasaRow(idx, 'hasil_pekerjaan', e.target.value)}
                        placeholder="Hasil" style={{ width: '100%', padding: '3px 4px', border: 'none', fontSize: '0.75rem' }} />
                    </td>
                    <td style={{ border: '1px solid #ccc', padding: 2, textAlign: 'center' }}>
                      <button className="btn-icon btn-danger" onClick={() => removeJasaRow(idx)}
                        style={{ width: 22, height: 22, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                        <X size={9} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>


        </div>
      </div>

      {/* ============================================
          SECTION 4: WAKTU PENGERJAAN & TTD
          ============================================ */}
      <div style={sectionStyle}>
        <div style={sectionHeaderStyle}>
          PENYELESAIAN PEKERJAAN
        </div>
        <div style={sectionBodyStyle}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 16 }}>
            <div>
              <div style={fieldLabelStyle}>Waktu Pengerjaan</div>
              <input type="datetime-local" value={form.waktu_penyelesaian}
                onChange={e => setForm({ ...form, waktu_penyelesaian: e.target.value })}
                style={fieldInputStyle} />
            </div>
            <div>
              <div style={{
                fontSize: '0.78rem', fontStyle: 'italic', color: '#555',
                padding: '8px 12px', background: '#f9f9f9', border: '1px solid #ddd',
                marginBottom: 10, lineHeight: 1.5,
              }}>
                Dengan ini dinyatakan bahwa pekerjaan telah selesai dikerjakan dan diterima dengan baik sesuai checklist catatan di atas.
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <div style={fieldLabelStyle}>TTD Pengawas / Pramudi</div>
                  <SignaturePad value={form.tanda_tangan_pengawas || ''} onChange={v => setForm(f => ({ ...f, tanda_tangan_pengawas: v }))} height={90} />
                </div>
                <div>
                  <div style={fieldLabelStyle}>TTD Teknisi</div>
                  <SignaturePad value={form.tanda_tangan_teknisi || ''} onChange={v => setForm(f => ({ ...f, tanda_tangan_teknisi: v }))} height={90} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ============================================
          SECTION 5: PERNYATAAN & TAGIHAN
          ============================================ */}
      <div style={sectionStyle}>
        <div style={sectionHeaderStyle}>
          VERIFIKASI & PENETAPAN TAGIHAN
        </div>
        <div style={sectionBodyStyle}>
          {/* Statement */}
          <div style={{
            fontSize: '0.8rem', fontStyle: 'italic', color: '#333',
            padding: '10px 14px', background: '#fef9ef', border: '1px solid #fde68a',
            borderLeft: '3px solid #d97706', marginBottom: 14, lineHeight: 1.6,
          }}>
            <strong>Pernyataan Pemeriksaan Hasil Pekerjaan:</strong><br/>
            "Dengan ini dinyatakan bahwa laporan pekerjaan telah diperiksa dan diverifikasi kebenarannya berdasarkan hasil pemeriksaan tersebut, ditetapkan nilai tagihan sebagaimana tercantum di bawah ini."
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <div style={fieldLabelStyle}>Nominal Tagihan (Rp)</div><input type="text" value={grandTotal.toLocaleString('id-ID')} readOnly
  placeholder="0"
  style={{ ...fieldInputStyle, fontWeight: 700, fontSize: '0.95rem', color: '#1aae6f' }} />
            </div>
            <div>
              <div style={fieldLabelStyle}>Diperiksa & Diterima Oleh</div>
              <input type="text" value={form.verified_by || ''} onChange={e => setForm({ ...form, verified_by: e.target.value })}
                placeholder="Achmad Saudi" style={fieldInputStyle} />
              <div style={{ marginTop: 4, display: 'flex', gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <div style={fieldLabelStyle}>Jabatan</div>
                  <input type="text" value={form.verified_jabatan || 'Direktur Operasional'}
                    onChange={e => setForm({ ...form, verified_jabatan: e.target.value })}
                    placeholder="Direktur Operasional" style={fieldInputStyle} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={fieldLabelStyle}>Tanggal</div>
                  <input type="date" value={form.verified_date || new Date().toISOString().slice(0, 10)}
                    onChange={e => setForm({ ...form, verified_date: e.target.value })}
                    style={fieldInputStyle} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ============================================
          DOKUMENTASI (Multi Foto & Video)
          ============================================ */}
      <div style={{ marginBottom: 20, border: '1px solid var(--gray-200)', borderRadius: 8, overflow: 'hidden' }}>
        <div style={{ padding: '10px 16px', fontWeight: 600, fontSize: '0.82rem', color: 'var(--gray-600)', background: '#fafafa', borderBottom: '1px solid var(--gray-100)' }}>
          📎 Dokumentasi (Foto & Video) — Opsional — Bisa multi-file
        </div>
        <div style={{ padding: 12, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <MultiFileUpload
            label="Foto"
            fileType="foto"
            urls={form.foto_urls}
            uploading={uploading.foto}
            onUpload={(files) => handleMultiUpload(files, 'foto')}
            onRemove={(idx) => handleRemoveFile(idx, 'foto')}
          />
          <MultiFileUpload
            label="Video"
            fileType="video"
            urls={form.video_urls}
            uploading={uploading.video}
            onUpload={(files) => handleMultiUpload(files, 'video')}
            onRemove={(idx) => handleRemoveFile(idx, 'video')}
          />
        </div>
      </div>

      {/* TOMBOL ACTION */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, borderTop: '2px solid #1a1a2e', paddingTop: 16, marginTop: 8 }}>
        {editingId && (
          <button className="btn btn-secondary" onClick={() => { setForm({ ...emptyForm, waktu_pemeriksaan: getWIBDateTime(), waktu_penyelesaian: getWIBDateTime(), nama_teknisi: user?.full_name || '' }); if (onCancelEdit) onCancelEdit(); }}>
            <X size={16} /><span>Batal Edit</span>
          </button>
        )}
        <button className="btn btn-secondary" onClick={() => handleSave(editingId ? (form.status || 'Pengajuan') : 'Pengajuan')} disabled={saving || uploading.foto || uploading.video}>
          <Save size={16} /><span>{saving ? 'Menyimpan...' : editingId ? 'Simpan Perubahan' : 'Simpan Draft'}</span>
        </button>
        {!(editingId && form.status === 'Selesai') && (
        <button className="btn btn-primary" onClick={() => handleSave(editingId ? (form.status || 'Menunggu Approval') : 'Menunggu Approval')} disabled={saving || uploading.foto || uploading.video}
          style={{ background: 'linear-gradient(135deg, #fb6340, #f5365c)', border: 'none' }}>
          <Send size={16} /><span>{saving ? 'Menyimpan...' : 'Kirim Pengajuan'}</span>
        </button>
        )}
      </div>
    </div>
  );
}

// ====== Tab 2: CM Disetujui ======
function TabStatus({ data, onRefresh, onViewDetail }) {
  const [progressModal, setProgressModal] = useState(null);

  return (
    <div>
      <div className="table-wrapper">
        <table className="data-table">
          <thead>
            <tr><th>No. CM</th><th>Tanggal</th><th>Rute</th><th>No. Body</th><th>Nama Klien</th><th>Ketua Koperasi</th><th>Teknisi</th><th>Detail Pekerjaan</th><th>Disetujui Oleh</th><th>Keterangan</th><th>Total</th><th>Progres</th><th style={{ width: 140, textAlign: 'center' }}>Aksi</th>
            </tr>
          </thead>
          <tbody>
            {data.map(d => {
              const gTotal = (d.sparepart_digunakan || []).reduce((s, sp) => s + (sp.jumlah || 0) * (sp.harga || 0), 0) +
                (d.jasa_digunakan || []).reduce((s, js) => s + (js.jumlah || 0) * (js.harga || 0), 0);
              const displayTotal = d.nominal_tagihan || gTotal;
              return (
              <tr key={d.id}>
                <td><code style={{ fontSize: '0.75rem', fontWeight: 600 }}>{d.cm_id || `#${d.id}`}</code></td>
                <td style={{ whiteSpace: 'nowrap', fontSize: '0.78rem' }}>{toWIBDate(d.created_at)}</td>
                <td style={{ fontSize: '0.8rem' }}>{d.rute || '-'}</td>
                <td style={{ fontSize: '0.8rem' }}>{d.no_body || '-'}</td>
                <td style={{ fontSize: '0.8rem', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.nama_klien || '-'}</td>
                <td style={{ fontSize: '0.78rem', maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--gray-600)' }}>{d.ketua_koperasi || '-'}</td>
                <td style={{ fontSize: '0.8rem', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.nama_teknisi || '-'}</td>
                <td style={{ fontSize: '0.78rem', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.detail_pekerjaan || '-'}</td>
                <td style={{ fontSize: '0.75rem', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.disetujui_oleh || '-'}</td>
                <td style={{ fontSize: '0.75rem', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.catatan || '-'}</td>
                <td style={{ fontSize: '0.78rem', fontWeight: 600, color: '#1aae6f', textAlign: 'right' }}>Rp {(displayTotal || 0).toLocaleString('id-ID')}</td>
              <td style={{ fontSize: '0.78rem' }}>
                {d.progress != null ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{
                      flex: 1, height: 6, borderRadius: 3,
                      background: '#e5e7eb', overflow: 'hidden',
                    }}>
                      <div style={{
                        width: `${d.progress}%`, height: '100%',
                        borderRadius: 3,
                        background: d.progress >= 100 ? '#2dce89' : d.progress >= 50 ? '#f59e0b' : '#fb6340',
                        transition: 'width 0.3s',
                      }} />
                    </div>
                    <span style={{ fontWeight: 600, fontSize: '0.72rem', color: '#374151', minWidth: 36, textAlign: 'right' }}>{d.progress}%</span>
                  </div>
                ) : (
                  <span style={{ color: '#9ca3af', fontSize: '0.72rem' }}>-</span>
                )}
              </td>
                <td style={{ textAlign: 'center' }}>
                  <div className="action-btns" style={{ justifyContent: 'center' }}>
                    <button className="btn-icon" title="Lihat Detail" onClick={() => onViewDetail && onViewDetail(d)} style={{ color: '#5e72e4' }}>
                      <Eye size={14} />
                    </button>
                      <button className="btn-icon" title="Update Progress" onClick={() => setProgressModal(d)} style={{ color: '#11cdef' }}>
                        <BarChart3 size={14} />
                      </button>
                      <button className="btn-icon" title="Selesaikan Pekerjaan" onClick={async () => {
                        if (window.confirm(`Yakin ingin menyelesaikan CM ${d.cm_id || '#' + d.id}?`)) {
                          try {
                            await selesaikanCM(d.id, { status: 'Selesai' });
                            onRefresh();
                          } catch (err) {
                            alert('Gagal: ' + (err.response?.data?.error || err.message));
                          }
                        }
                      }} style={{ color: '#2dce89' }}>
                        <CheckCircle size={14} />
                      </button>
                  </div>
                </td>
              </tr>
            );})}
            {data.length === 0 && <tr><td colSpan={10} style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--gray-400)' }}>Tidak ada CM dalam proses</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Progress Modal */}
      {progressModal && <ProgressModal item={progressModal} onClose={() => setProgressModal(null)} onSaved={() => { setProgressModal(null); onRefresh(); }} />}
    </div>
  );
}

// ====== Progress Update Modal ======
function ProgressModal({ item, onClose, onSaved }) {
  const [progress, setProgress] = useState(item.progress || 0);
  const [status, setStatus] = useState(item.status);
  const [saving, setSaving] = useState(false);

  const statusOptions = STATUS_OPTIONS;
  const handleSave = async () => {
    setSaving(true);
    try {
      if (status === 'Selesai') {
        await selesaikanCM(item.id, { status: 'Selesai' });
      } else {
        await updateCMProgress(item.id, { progress, status });
      }
      onSaved();
    } catch (err) {
      alert('Gagal update: ' + (err.response?.data?.error || err.message));
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose} style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1000 }}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 450, borderRadius: 12 }}>
        <div className="modal-header" style={{ background: 'linear-gradient(135deg, #11cdef, #0ea5e9)', color: '#fff', borderRadius: '12px 12px 0 0', padding: '14px 20px' }}>
          <div>
            <h3 style={{ color: '#fff', margin: 0, fontSize: '1rem' }}>Update Progress</h3>
            <small style={{ opacity: 0.85 }}>{item.cm_id || `#${item.id}`} — {item.no_polisi}</small>
          </div>
          <button className="btn-icon" onClick={onClose} style={{ color: '#fff' }}><X size={20} /></button>
        </div>
        <div className="modal-body" style={{ padding: '20px 24px', background: '#fff' }}>
          <div className="form-group" style={{ marginBottom: 16 }}>
            <label>Status Pekerjaan</label>
            <select value={status} onChange={e => setStatus(e.target.value)} style={{ fontWeight: 600, color: (statusConfig[status] || {}).color }}>
              <option value="">-- Pilih Status --</option>
              {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: 16 }}>
            <label>Progress ({progress}%)</label>
            <input type="range" min={0} max={100} value={progress} onChange={e => setProgress(Number(e.target.value))} style={{ width: '100%' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', color: 'var(--gray-400)' }}>
              <span>0%</span><span>25%</span><span>50%</span><span>75%</span><span>100%</span>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: '#eef9fd', borderRadius: 8, fontSize: '0.82rem', color: '#0a6b9e' }}>
            <Clock size={16} />
            <span>Status saat ini: <strong>{item.status}</strong> | Progress: <strong>{item.progress || 0}%</strong></span>
          </div>
        </div>
        <div className="modal-footer" style={{ padding: '12px 24px', borderTop: '1px solid var(--gray-100)', display: 'flex', justifyContent: 'flex-end', gap: 10, background: '#fafafa', borderRadius: '0 0 12px 12px' }}>
          <button className="btn btn-secondary" onClick={onClose}>Batal</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving} style={{ background: 'linear-gradient(135deg, #11cdef, #0ea5e9)', border: 'none' }}>
            {saving ? 'Menyimpan...' : 'Update Progress'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ====== MAIN PAGE ======
export default function CM() {
  const { hasMenuAction } = useAuth();
  const [activeTab, setActiveTab] = useState('submission');
  const [data, setData] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [spareparts, setSpareparts] = useState([]);
  const [jasaLayanan, setJasaLayanan] = useState([]);
  const [karyawanList, setKaryawanList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailItem, setDetailItem] = useState(null);
  // Handler: fetch full detail CM (termasuk foto_urls/video_urls yang tidak ada di list)
  const handleViewDetail = useCallback(async (item) => {
    // Set dulu dengan data list untuk render instan
    setDetailItem(item);
    // Lalu fetch detail lengkap dari API untuk dapat foto_urls, video_urls, dll
    // CATATAN: getCMById mengembalikan axios response ({data: ...}), jadi ambil .data
    try {
      const res = await getCMById(item.id);
      const fullDetail = res?.data;
      if (fullDetail) {
        setDetailItem(prev => prev?.id === item.id ? { ...prev, ...fullDetail } : prev);
      }
    } catch (e) {
      console.warn('Gagal fetch detail CM:', e);
      // Tetap pakai data list jika gagal
    }
  }, []);

  // Handler: edit CM dari tab CM Selesai
  // Fetch detail lengkap DULU (foto_urls/video_urls TIDAK ada di response list)
  // dan form baru dibuka SETELAH data lengkap tersedia — mencegah foto terhapus
  // jika admin menyimpan sebelum data foto dimuat.
  const handleEditCompletedCM = useCallback(async (item) => {
    setEditLoadingId(item.id);
    try {
      const res = await getCMById(item.id);
      const fullDetail = res?.data || item;
      setEditingId(item.id);
      setEditingData(fullDetail);
      setShowForm(true);
    } catch (e) {
      console.warn('Gagal fetch detail CM untuk edit:', e);
      alert('Gagal memuat data CM. Silakan coba lagi.');
    } finally {
      setEditLoadingId(null);
    }
  }, []);

  const [editingId, setEditingId] = useState(null);
  const [editingData, setEditingData] = useState(null);
    const [showForm, setShowForm] = useState(false);
  const [showExportFilter, setShowExportFilter] = useState(false);
  const [exportTglAwal, setExportTglAwal] = useState('');
  const [exportTglAkhir, setExportTglAkhir] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [pmInitData, setPmInitData] = useState(null);
  const [tagihanCMList, setTagihanCMList] = useState([]);
  const [editLoadingId, setEditLoadingId] = useState(null);

  // ====== PAGINATION STATE ======
  const [page, setPage] = useState(1);
  const [perPage] = useState(50);
  const [total, setTotal] = useState(0);
  const totalPages = Math.ceil(total / perPage) || 1;

  // Lookup map: cm_id → tagihan (for metode_pembayaran & tgl_bayar)

  // ====== GLOBAL FILTER STATE (defined early so useCallback deps below can reference them) ======
  const [globalSearch, setGlobalSearch] = useState('');
  const [globalStatusFilter, setGlobalStatusFilter] = useState('');
  const [tanggalAwal, setTanggalAwal] = useState('');
  const [tanggalAkhir, setTanggalAkhir] = useState('');
  const [approveModal, setApproveModal] = useState(null); // CM item to approve
  const [approveRole, setApproveRole] = useState('Pemilik');
  const [approveKeterangan, setApproveKeterangan] = useState('');
  const [rejectModal, setRejectModal] = useState(null); // CM item to reject
  const [rejectRole, setRejectRole] = useState('Pemilik');
  const [rejectKeterangan, setRejectKeterangan] = useState('');
  // ====== DETAILED FILTER STATE (seperti di PM) ======
  const [filterNoBody, setFilterNoBody] = useState('');
  const [filterNoPolisi, setFilterNoPolisi] = useState('');
  const [filterTeknisi, setFilterTeknisi] = useState('');
  const [filterLokasi, setFilterLokasi] = useState('');
  // Multi-pilih (array): Nama Klien & No Kontrak — disaring di SERVER agar berlaku
  // untuk seluruh data CM, bukan hanya 50 baris halaman yang sedang tampil.
  const [filterNamaKlien, setFilterNamaKlien] = useState([]);
  const [filterNoKontrak, setFilterNoKontrak] = useState([]);
  const [filterOptions, setFilterOptions] = useState({ klien: [], no_kontrak: [] });
  const [filterJenisKendaraan, setFilterJenisKendaraan] = useState('');

  // ====== DEBOUNCED SEARCH ======
  const [searchInput, setSearchInput] = useState('');
  const searchTimerRef = useRef(null);
  const handleSearchChange = useCallback((e) => {
    const val = e.target.value;
    setSearchInput(val);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setGlobalSearch(val);
      setPage(1); // Reset to page 1 on new search
    }, 400); // 400ms debounce
  }, []);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, []);

  // Reset to page 1 when switching tabs
  useEffect(() => { setPage(1); }, [activeTab]);

  // ====== FAST LOAD: fetch CM data with pagination + search + tab ======
  const load = useCallback(() => {
    setLoading(true);
    const params = { page, per_page: perPage };
    if (globalSearch) params.search = globalSearch;
    if (activeTab && activeTab !== 'remainder') params.tab = activeTab;
    // Filter multi-pilih dikirim ke server (dipisah koma)
    if (filterNamaKlien.length > 0) params.klien = filterNamaKlien.join(',');
    if (filterNoKontrak.length > 0) params.no_kontrak = filterNoKontrak.join(',');
    Promise.allSettled([
      getCM(params).then(res => {
        const d = res.data;
        if (d && d.filter_options) setFilterOptions(d.filter_options);
        if (d && Array.isArray(d.data)) {
          setData(d.data);
          setTotal(d.total || 0);
        } else if (Array.isArray(d)) {
          // Fallback: old format (no pagination)
          setData(d);
          setTotal(d.length);
        } else {
          setData([]);
          setTotal(0);
        }
      }).catch(err => console.error('getCM error:', err)),
      // Muat daftar tagihan agar tanda "Sudah Dibuat Tagihan" muncul di CM Selesai
      getTagihan().then(tagRes => {
        const tagData = Array.isArray(tagRes.data) ? tagRes.data : [];
        setTagihanCMList(tagData);
      }).catch(err => console.error('getTagihan error:', err)),
    ]).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage, globalSearch, activeTab, filterNamaKlien.join(','), filterNoKontrak.join(',')]);

  // Pilihan filter: gabungan opsi dari server (seluruh data CM) + yang terlihat di halaman
  // ini. Cadangan dari halaman dipakai agar tetap jalan bila backend belum mengirim
  // `filter_options` (mis. bundle lama).
  const klienFilterOptions = React.useMemo(() => {
    const set = new Set(filterOptions.klien || []);
    data.forEach(d => { const k = (d.nama_klien || '').trim(); if (k) set.add(k); });
    return Array.from(set).sort();
  }, [filterOptions, data]);

  const kontrakFilterOptions = React.useMemo(() => {
    const set = new Set(filterOptions.no_kontrak || []);
    data.forEach(d => { const k = String(d.no_kontrak || '').trim(); if (k) set.add(k); });
    return Array.from(set).sort();
  }, [filterOptions, data]);

  // ====== LAZY LOAD: fetch form data only when form is opened ======
  const loadFormData = useCallback(() => {
    if (vehicles.length > 0) return; // Already loaded
    Promise.allSettled([
      getVehicles({ all: true }).then(vehRes => {
        setVehicles(vehRes.data?.data || []);
      }).catch(err => console.error('getVehicles error:', err)),
      getInventoriSpareparts().then(spRes => {
        setSpareparts(spRes.data || []);
      }).catch(err => console.error('getSpareparts error:', err)),
      getJasaLayanan().then(jasaRes => {
        setJasaLayanan(jasaRes.data || []);
      }).catch(err => console.error('getJasa error:', err)),
      getKaryawan().then(karyRes => {
        setKaryawanList(karyRes.data || []);
      }).catch(err => console.error('getKaryawan error:', err)),
      getTagihan().then(tagRes => {
        const tagData = Array.isArray(tagRes.data) ? tagRes.data : [];
        setTagihanCMList(tagData);
      }).catch(err => console.error('getTagihan error:', err)),
    ]);
  }, [vehicles.length]);

  // Initial load
  useEffect(() => { setShowForm(false); load(); }, [load]);

  // Load form data when form is opened or editing or coming from PM
  useEffect(() => {
    if (showForm || editingId || pmInitData) {
      loadFormData();
    }
  }, [showForm, editingId, pmInitData, loadFormData]);

  // Refresh data when refreshKey changes (after save/create/delete)
  useEffect(() => {
    if (refreshKey > 0) {
      load();
      loadFormData();
    }
  }, [refreshKey, load, loadFormData]);

  // Handle URL params from PM (from_pm, teknisi, polisi, body, pengawas, vehicle_id, catatan)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const fromPm = params.get('from_pm');
    if (fromPm) {
      setPmInitData({
        pm_id: fromPm,
        vehicle_id: params.get('vehicle_id') || '',
        no_body: params.get('body') || '',
        no_polisi: params.get('polisi') || '',
        nama_teknisi: params.get('teknisi') || '',
        pengawas_pramudi: params.get('pengawas') || '',
        detail_pekerjaan: params.get('catatan') || '',
      });
      setShowForm(true);
      window.history.replaceState({}, document.title, '/cm');
    }
  }, []);

  // Helper: check if a date string falls within the date range
  const inDateRange = useCallback((dateStr) => {
    if (!tanggalAwal && !tanggalAkhir) return true;
    if (!dateStr) return true;
    const d = dateStr.slice(0, 10);
    if (tanggalAwal && d < tanggalAwal) return false;
    if (tanggalAkhir && d > tanggalAkhir) return false;
    return true;
  }, [tanggalAwal, tanggalAkhir]);

  // ====== REMAINDER DATA: Filter Dryer 6 bulan reminder (for AC vehicles) ======
  // Fungsi pembantu: hitung data reminder filter dryer (dipakai tab Remainder
  // DAN export). Diekstrak agar export memakai dataset LENGKAP, bukan hanya
  // halaman yang sedang tampil di datalist.
  const computeRemainderData = useCallback((list) => {
    const now = new Date();
    const filteredCM = (Array.isArray(list) ? list : []).filter(d => {
      if (d.status !== 'Selesai') return false;
      const sp = d.sparepart_digunakan || [];
      if (!sp.some(s => (s.nama || '').toLowerCase().includes('filter dryer'))) return false;
      if ((d.jenis_kendaraan || '').toUpperCase() !== 'AC') return false;
      return true;
    });
    return filteredCM.map(d => {
      const sp = d.sparepart_digunakan || [];
      const filterDryerItem = sp.find(s => (s.nama || '').toLowerCase().includes('filter dryer'));
      const replaceDate = new Date(d.tanggal_penyelesaian || d.created_at || d.waktu_penyelesaian || d.waktu_pemeriksaan || Date.now());
      const dueDate = new Date(replaceDate);
      dueDate.setMonth(dueDate.getMonth() + 6);
      const reminderStart = new Date(dueDate);
      reminderStart.setDate(reminderStart.getDate() - 14);
      const daysUntilDue = Math.ceil((dueDate - now) / (1000 * 60 * 60 * 24));
      let reminderStatus, statusColor;
      if (now >= dueDate) { reminderStatus = 'Terlewat'; statusColor = '#f5365c'; }
      else if (now >= reminderStart) { reminderStatus = 'Perlu Perawatan'; statusColor = '#fb6340'; }
      else { reminderStatus = `${daysUntilDue} hari lagi`; statusColor = '#2dce89'; }
      return { ...d, replaceDate, dueDate, daysUntilDue, reminderStatus, statusColor, filterDryerNama: filterDryerItem?.nama || 'Filter Dryer' };
    }).sort((a, b) => a.daysUntilDue - b.daysUntilDue);
  }, []);

  const remainderData = React.useMemo(() => computeRemainderData(data), [data, computeRemainderData]);

  // Helper: apply detailed filters to a CM item
  const applyDetailFilters = (d) => {
    if (filterNoBody && !d.no_body?.toLowerCase().includes(filterNoBody.toLowerCase())) return false;
    if (filterNoPolisi && !d.no_polisi?.toLowerCase().includes(filterNoPolisi.toLowerCase())) return false;
    if (filterTeknisi && !d.nama_teknisi?.toLowerCase().includes(filterTeknisi.toLowerCase())) return false;
    if (filterLokasi && !d.lokasi_pengerjaan?.toLowerCase().includes(filterLokasi.toLowerCase())) return false;
    if (filterNamaKlien.length > 0 && !filterNamaKlien.includes((d.nama_klien || '').trim())) return false;
    if (filterNoKontrak.length > 0 && !filterNoKontrak.includes(String(d.no_kontrak || '').trim())) return false;
    if (filterJenisKendaraan) {
      const jenisAc = d.jenis_ac || '';
      if (jenisAc !== filterJenisKendaraan) return false;
    }
    return true;
  };

  // Filtered data for each tab (with date range)
  const submissionFiltered = data.filter(d => {
    if (!['Pengajuan', 'Menunggu Approval'].includes(d.status)) return false;
    if (!applyDetailFilters(d)) return false;
    if (!inDateRange(d.created_at)) return false;
    if (!globalSearch) return true;
    const q = globalSearch.toLowerCase();
    return d.cm_id?.toLowerCase().includes(q) || d.no_polisi?.toLowerCase().includes(q) || d.nama_teknisi?.toLowerCase().includes(q) || d.nama_klien?.toLowerCase().includes(q);
  });

  const approvedFiltered = data.filter(d => {
    if (!['Diproses', 'Menunggu Sparepart', 'Sedang Dikerjakan', 'Pending'].includes(d.status)) return false;
    if (!applyDetailFilters(d)) return false;
    if (!inDateRange(d.created_at)) return false;
    if (globalSearch) {
      const q = globalSearch.toLowerCase();
      if (!d.cm_id?.toLowerCase().includes(q) && !d.no_polisi?.toLowerCase().includes(q) && !d.nama_teknisi?.toLowerCase().includes(q) && !d.nama_klien?.toLowerCase().includes(q)) return false;
    }
    if (globalStatusFilter && d.status !== globalStatusFilter) return false;
    return true;
  });

  const rejectedFiltered = data.filter(d => {
    if (d.status !== 'Ditolak') return false;
    if (!applyDetailFilters(d)) return false;
    if (!inDateRange(d.created_at)) return false;
    if (!globalSearch) return true;
    const q = globalSearch.toLowerCase();
    return d.cm_id?.toLowerCase().includes(q) || d.no_polisi?.toLowerCase().includes(q) || d.nama_teknisi?.toLowerCase().includes(q) || d.nama_klien?.toLowerCase().includes(q);
  });

  const completedFiltered = data.filter(d => {
    if (d.status !== 'Selesai') return false;
    if (!applyDetailFilters(d)) return false;
    if (!inDateRange(d.created_at)) return false;
    if (!globalSearch) return true;
    const q = globalSearch.toLowerCase();
    return d.cm_id?.toLowerCase().includes(q) || d.no_polisi?.toLowerCase().includes(q) || d.nama_teknisi?.toLowerCase().includes(q) || d.nama_klien?.toLowerCase().includes(q);
  });

  // Tab counts — gunakan filtered data agar count sesuai dengan isi tabel
  const tabCounts = {
    submission: submissionFiltered.length,
    approved: approvedFiltered.length,
    rejected: rejectedFiltered.length,
    completed: completedFiltered.length,
    remainder: remainderData.length,
  };

  // ====== Build CM Report per Teknisi ======
  const buildCMReport = useCallback((dateFilter, mainFilters, sourceData) => {
    const { startDate, endDate } = dateFilter || {};
    const inDateRange = (d) => {
      if (!startDate && !endDate) return true;
      if (!d) return true;
      const dateStr = d.slice(0, 10);
      if (startDate && dateStr < startDate) return false;
      if (endDate && dateStr > endDate) return false;
      return true;
    };

    // Helper to apply main filters to a CM record
    const {
      filterNoBody: fNoBody,
      filterNoPolisi: fNoPolisi,
      filterTeknisi: fTeknisi,
      filterLokasi: fLokasi,
      filterNamaKlien: fNamaKlien,
      filterNoKontrak: fNoKontrak,
      filterJenisKendaraan: fJenisKendaraan,
      tanggalAwal: fTglAwal,
      tanggalAkhir: fTglAkhir,
    } = mainFilters || {};
    const passMainFilters = (d) => {
      if (fNoBody && !d.no_body?.toLowerCase().includes(fNoBody.toLowerCase())) return false;
      if (fNoPolisi && !d.no_polisi?.toLowerCase().includes(fNoPolisi.toLowerCase())) return false;
      if (fTeknisi && !d.nama_teknisi?.toLowerCase().includes(fTeknisi.toLowerCase())) return false;
      if (fLokasi && !d.lokasi_pengerjaan?.toLowerCase().includes(fLokasi.toLowerCase())) return false;
      if (fNamaKlien && fNamaKlien.length > 0 && !fNamaKlien.includes((d.nama_klien || '').trim())) return false;
      if (fNoKontrak && fNoKontrak.length > 0 && !fNoKontrak.includes(String(d.no_kontrak || '').trim())) return false;
      if (fJenisKendaraan) {
        const jenisAc = d.jenis_ac || '';
        if (jenisAc !== fJenisKendaraan) return false;
      }
      if (fTglAwal && d.created_at && d.created_at.slice(0, 10) < fTglAwal) return false;
      if (fTglAkhir && d.created_at && d.created_at.slice(0, 10) > fTglAkhir) return false;
      return true;
    };

    const baseData = Array.isArray(sourceData) ? sourceData : data;
    const teknisiMap = {};
    baseData.forEach(d => {
      if (!d || !inDateRange(d.created_at)) return;
      if (!passMainFilters(d)) return;
      const nama = d.nama_teknisi || 'Tanpa Teknisi';
      if (!teknisiMap[nama]) {
        teknisiMap[nama] = { nama, total: 0, selesai: 0, dalamProses: 0, ditolak: 0, pengajuan: 0, items: [] };
      }
      teknisiMap[nama].total++;
      teknisiMap[nama].items.push(d);
      const status = (d.status || '').toLowerCase();
      if (status === 'selesai') teknisiMap[nama].selesai++;
      else if (status === 'ditolak') teknisiMap[nama].ditolak++;
      else if (status === 'pengajuan' || status === 'menunggu approval') teknisiMap[nama].pengajuan++;
      else teknisiMap[nama].dalamProses++;
    });

    return Object.values(teknisiMap);
  }, [data]);

  const escapeCSV = (str) => {
    if (str == null) return '';
    const s = String(str);
    // Hapus newline/carriage return dalam sel (ganti spasi) agar tidak merusak baris CSV
    const cleaned = s.replace(/[\r\n]+/g, ' ').trim();
    if (cleaned.includes(',') || cleaned.includes('"') || cleaned.includes('\n') || cleaned.includes('\r')) {
      return '"' + cleaned.replace(/"/g, '""') + '"';
    }
    return cleaned;
  };

  const fmtPeriod = (d) => {
    if (!d) return '';
    try {
      return new Date(d + 'T00:00:00').toLocaleDateString('id-ID', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch(e) { return d; }
  };

  // Ambil SEMUA data CM dari server (loop semua halaman, 500/halaman).
  // Backend membatasi 500 baris/request & halaman aktif hanya 50 baris —
  // export TIDAK boleh terbatas pada data halaman yang sedang tampil.
  const fetchAllCMRecords = async () => {
    const all = [];
    const perReq = 500;
    let pageIdx = 1;
    let total = Infinity;
    while (all.length < total) {
      const res = await getCM({ page: pageIdx, limit: perReq });
      const d = res && res.data;
      const items = (d && Array.isArray(d.data)) ? d.data : (Array.isArray(d) ? d : []);
      if (items.length === 0) break;
      all.push(...items);
      total = (d && typeof d.total === 'number') ? d.total : all.length;
      pageIdx += 1;
      if (pageIdx > 100) break; // pengaman anti-loop tak terbatas
    }
    return all;
  };

  const exportCSV = async () => {
    try {
      // Ambil data karyawan langsung dari API (pastikan selalu fresh)
      const karyRes = await getKaryawan();
      const karyawanData = Array.isArray(karyRes.data) ? karyRes.data : [];

      // Export memakai SEMUA data CM dari server (loop semua halaman) —
      // sebelumnya hanya memakai data halaman aktif (50 baris) sehingga data
      // tidak lengkap dan filter tanggal export diabaikan.
      const allData = await fetchAllCMRecords();

      // Filter tanggal EXPORT (tombol Filter di samping tombol Excel/PDF)
      const exportDateFilter = (d) => {
        const tgl = d.created_at ? d.created_at.slice(0, 10) : '';
        if (exportTglAwal && tgl && tgl < exportTglAwal) return false;
        if (exportTglAkhir && tgl && tgl > exportTglAkhir) return false;
        return true;
      };
      const matchSearch = (d) => {
        if (!globalSearch) return true;
        const q = globalSearch.toLowerCase();
        return d.cm_id?.toLowerCase().includes(q) || d.no_polisi?.toLowerCase().includes(q) || d.nama_teknisi?.toLowerCase().includes(q) || d.nama_klien?.toLowerCase().includes(q);
      };

      // Pilih data sesuai tab aktif + SEMUA filter (detail, tanggal utama,
      // tanggal export, pencarian) — sama seperti isi datalist tapi LENGKAP.
      let sourceData = [];
      switch (activeTab) {
        case 'submission':
          sourceData = allData.filter(d =>
            ['Pengajuan', 'Menunggu Approval'].includes(d.status) &&
            applyDetailFilters(d) && inDateRange(d.created_at) && exportDateFilter(d) && matchSearch(d));
          break;
        case 'approved':
          sourceData = allData.filter(d =>
            ['Diproses', 'Menunggu Sparepart', 'Sedang Dikerjakan', 'Pending'].includes(d.status) &&
            applyDetailFilters(d) && inDateRange(d.created_at) && exportDateFilter(d) && matchSearch(d) &&
            (!globalStatusFilter || d.status === globalStatusFilter));
          break;
        case 'rejected':
          sourceData = allData.filter(d =>
            d.status === 'Ditolak' && applyDetailFilters(d) &&
            inDateRange(d.created_at) && exportDateFilter(d) && matchSearch(d));
          break;
        case 'completed':
          sourceData = allData.filter(d =>
            d.status === 'Selesai' && applyDetailFilters(d) &&
            inDateRange(d.created_at) && exportDateFilter(d) && matchSearch(d));
          break;
        case 'remainder':
          sourceData = computeRemainderData(allData);
          break;
        default:
          sourceData = allData;
      }

      if (sourceData.length === 0) {
        alert('Tidak ada data CM untuk diexport.');
        return;
      }

      // Helper: format datetime ke WIB (DD/MM/YYYY HH:MM) — format Indonesia
      const fmtWIB = (dt) => {
        if (!dt) return '';
        const d = new Date(dt);
        const f = new Intl.DateTimeFormat('id-ID', {
          timeZone: 'Asia/Jakarta',
          year: 'numeric', month: '2-digit', day: '2-digit',
          hour: '2-digit', minute: '2-digit', hour12: false,
        });
        const parts = f.formatToParts(d);
        const p = (t) => parts.find(part => part.type === t)?.value || '00';
        return `${p('day')}/${p('month')}/${p('year')} ${p('hour')}:${p('minute')}`;
      };

      // Helper: hitung durasi antara dua datetime
      const calcDurasi = (mulai, selesai) => {
        if (!mulai || !selesai) return '';
        const t1 = new Date(mulai);
        const t2 = new Date(selesai);
        if (isNaN(t1.getTime()) || isNaN(t2.getTime())) return '';
        const diffMs = Math.max(0, t2 - t1);
        const hours = Math.floor(diffMs / (1000 * 60 * 60));
        const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
        if (hours > 0) return `${hours} jam ${minutes} menit`;
        return `${minutes} menit`;
      };

      // Helper: cari ID Teknisi dari data karyawan (fresh dari API)
      const cariIdTeknisi = (nama) => {
        if (!nama) return '';
        const cari = nama.trim().toLowerCase();
        let found = karyawanData.find(k => (k.nama || '').trim().toLowerCase() === cari);
        if (!found) {
          found = karyawanData.find(k => (k.nama || '').trim().toLowerCase().includes(cari) || cari.includes((k.nama || '').trim().toLowerCase()));
        }
        return found ? String(found.nik) : '';
      };

      // Helper: format tanggal saja (DD/MM/YYYY) — tanpa jam
      const fmtTanggal = (dt) => {
        if (!dt) return '';
        const d = new Date(dt);
        const f = new Intl.DateTimeFormat('id-ID', {
          timeZone: 'Asia/Jakarta',
          year: 'numeric', month: '2-digit', day: '2-digit',
        });
        const parts = f.formatToParts(d);
        const p = (t) => parts.find(part => part.type === t)?.value || '00';
        return `${p('day')}/${p('month')}/${p('year')}`;
      };

      // Bangun baris data flat (16 kolom)
      const rows = [];
      rows.push([
        'CM_ID', 'Nama Klien', 'No Body', 'No Polisi', 'Rute', 'Jenis AC/non AC',
        'Detail Pekerjaan', 'Sparepart/Jasa Layanan', 'Ganti Sparepart',
        'ID Teknisi', 'Nama Teknisi', 'Status',
        'Waktu Pengajuan CM', 'Waktu CM Selesai', 'Durasi (Jam-Menit)', 'Tanggal CM'
      ].join(','));

      sourceData.forEach(d => {
        const spNames = (d.sparepart_digunakan || []).map(s => s.nama).filter(Boolean);
        const jsNames = (d.jasa_digunakan || []).map(j => j.nama).filter(Boolean);
        const sparepartJasa = [...spNames, ...jsNames].join('; ');

        const tglPengajuan = fmtWIB(d.waktu_pemeriksaan || d.created_at);
        const tglSelesai = fmtWIB(d.waktu_penyelesaian);
        const durasi = calcDurasi(d.waktu_pemeriksaan || d.created_at, d.waktu_penyelesaian);
        // Tanggal CM dibuat — hanya tanggal (DD/MM/YYYY), tanpa jam
        const tglCM = d.created_at ? fmtTanggal(d.created_at) : '';

        const idTeknisi = cariIdTeknisi(d.nama_teknisi) || d.teknisi_handle || '';
        const adaSparepart = (d.sparepart_digunakan || []).length > 0;

        rows.push([
          escapeCSV(d.cm_id || `#${d.id}`),
          escapeCSV(d.nama_klien || ''),
          escapeCSV(d.no_body || ''),
          escapeCSV(d.no_polisi || ''),
          escapeCSV(d.rute || ''),
          escapeCSV(d.jenis_ac || ''),
          escapeCSV(d.detail_pekerjaan || ''),
          escapeCSV(sparepartJasa),
          escapeCSV(adaSparepart ? 'Ya' : 'Tidak'),
          escapeCSV(idTeknisi),
          escapeCSV(d.nama_teknisi || ''),
          escapeCSV(d.status || ''),
          escapeCSV(tglPengajuan),
          escapeCSV(tglSelesai),
          escapeCSV(durasi),
          escapeCSV(tglCM),
        ].join(','));
      });

      const csvContent = rows.join('\n');
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const periodSuffix = exportTglAwal || exportTglAkhir
        ? `_${exportTglAwal || 'all'}_${exportTglAkhir || 'all'}`
        : '';
      const filename = `Laporan_CM${periodSuffix}_${new Date().toISOString().slice(0, 10)}.csv`;
      const link = document.createElement('a');
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Gagal ambil data untuk export:', err);
      alert('Gagal mengambil data untuk export. Coba lagi.');
    }
  };

  const exportPDF = async () => {
    // Export memakai SEMUA data CM dari server, bukan hanya halaman aktif
    let allData = [];
    try {
      allData = await fetchAllCMRecords();
    } catch (e) {
      console.error('Gagal ambil data CM untuk export:', e);
      alert('Gagal mengambil data CM. Coba lagi.');
      return;
    }
    const dateFilter = {};
    if (exportTglAwal) dateFilter.startDate = exportTglAwal;
    if (exportTglAkhir) dateFilter.endDate = exportTglAkhir;
    const mainFilters = {
      filterNoBody, filterNoPolisi, filterTeknisi, filterLokasi,
      filterNamaKlien, filterNoKontrak, filterJenisKendaraan, tanggalAwal, tanggalAkhir,
    };
    const report = buildCMReport(Object.keys(dateFilter).length > 0 ? dateFilter : undefined, mainFilters, allData);
    if (report.length === 0) {
      alert('Tidak ada data CM untuk diexport.');
      return;
    }

    const { jsPDF } = require('jspdf');
    const autoTable = require('jspdf-autotable').default || require('jspdf-autotable');
    const doc = new jsPDF('l', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();

    const primary = [251, 99, 64];
    const secondary = [245, 54, 92];
    const gray = [136, 152, 170];

    let pageNum = 1;
    const addFooter = () => {
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = pageNum; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(7);
        doc.setTextColor(...gray);
        doc.text(`Halaman ${i} dari ${pageCount}`, pageWidth - 20, 205, { align: 'right' });
        doc.text('Dicetak dari sistem LayananSynexa FMS', 15, 205);
      }
      pageNum = pageCount + 1;
    };

    doc.setFillColor(...primary);
    doc.rect(10, 10, pageWidth - 20, 22, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.text('LAPORAN CM PER TEKNISI', pageWidth / 2, 22, { align: 'center' });
    doc.setFontSize(8);
    doc.text('Corrective Maintenance Report — Fleet Service System', pageWidth / 2, 29, { align: 'center' });

    let startY = 40;
    const periodLabel = exportTglAwal || exportTglAkhir
      ? `Periode: ${fmtPeriod(exportTglAwal) || '∞'} — ${fmtPeriod(exportTglAkhir) || '∞'}`
      : '';
    if (periodLabel) {
      doc.setFontSize(9);
      doc.setTextColor(...secondary);
      doc.text(periodLabel, 15, 35);
      startY = 44;
    }

    doc.setFontSize(10);
    doc.setTextColor(50, 50, 50);
    doc.text('RINGKASAN PER TEKNISI', 15, startY - 4);
    const summaryRows = report.map(t => {
      const pct = t.total > 0 ? Math.round((t.selesai / t.total) * 100) : 0;
      return [t.nama, t.total, t.selesai, t.dalamProses, t.ditolak, t.pengajuan, `${pct}%`];
    });
    const grandTotal = report.reduce((s, t) => s + t.total, 0);
    const grandSelesai = report.reduce((s, t) => s + t.selesai, 0);
    const grandProses = report.reduce((s, t) => s + t.dalamProses, 0);
    const grandDitolak = report.reduce((s, t) => s + t.ditolak, 0);
    const grandPengajuan = report.reduce((s, t) => s + t.pengajuan, 0);
    const grandPct = grandTotal > 0 ? Math.round((grandSelesai / grandTotal) * 100) : 0;
    summaryRows.push(['GRAND TOTAL', grandTotal, grandSelesai, grandProses, grandDitolak, grandPengajuan, `${grandPct}%`]);

    autoTable(doc, {
      startY: startY,
      head: [['Teknisi', 'Total CM', 'Selesai', 'Dalam Proses', 'Ditolak', 'Pengajuan', 'Progres']],
      body: summaryRows,
      theme: 'grid',
      headStyles: { fillColor: primary, fontSize: 8, fontStyle: 'bold', halign: 'center' },
      bodyStyles: { fontSize: 7.5 },
      columnStyles: {
        0: { cellWidth: 55 },
        1: { halign: 'center' },
        2: { halign: 'center', fontStyle: 'bold', textColor: [45, 206, 137] },
        3: { halign: 'center', textColor: [17, 205, 239] },
        4: { halign: 'center', textColor: [245, 54, 92] },
        5: { halign: 'center', textColor: [251, 99, 64] },
        6: { halign: 'center' },
      },
      footStyles: { fillColor: [248, 249, 250], fontSize: 8, fontStyle: 'bold' },
      margin: { top: 35, bottom: 15 },
      didDrawPage: () => addFooter(),
    });

    const pageHeight = doc.internal.pageSize.getHeight();
    report.forEach(t => {
      const remainingSpace = pageHeight - doc.lastAutoTable.finalY - 20;
      if (remainingSpace < 40) {
        doc.addPage();
        autoTable(doc, {
          startY: 15,
          head: [['No CM', 'No Polisi', 'No Body', 'Klien', 'Status', 'Tgl Pengajuan', 'Detail Pekerjaan']],
          body: [['— Lanjutan —', '', '', '', '', '', '']],
          theme: 'grid',
          headStyles: { fillColor: primary, fontSize: 8, fontStyle: 'bold', halign: 'center' },
          bodyStyles: { fontSize: 7.5, fontStyle: 'italic' },
          margin: { top: 15, bottom: 15 },
          didDrawPage: () => addFooter(),
        });
      }

      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 10,
        body: [[`Teknisi: ${t.nama} — Total ${t.items.length} CM (Selesai: ${t.selesai}, Proses: ${t.dalamProses}, Ditolak: ${t.ditolak})`]],
        theme: 'plain',
        styles: { fontSize: 8, fontStyle: 'bold', fillColor: [255, 245, 238] },
        margin: { top: 15, bottom: 5 },
      });

      const detailRows = t.items.map(d => [
        d.cm_id || `#${d.id}`,
        d.no_polisi || '-',
        d.no_body || '-',
        d.nama_klien || '-',
        d.status || '-',
        toWIBDate(d.created_at),
        (d.sparepart_komponen || '-').replace(/,/g, ', '),
      ]);

      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 2,
        head: [['No CM', 'No Polisi', 'No Body', 'Klien', 'Status', 'Tgl Pengajuan', 'Detail Pekerjaan']],
        body: detailRows,
        theme: 'grid',
        headStyles: { fillColor: secondary, fontSize: 7.5, fontStyle: 'bold', halign: 'center' },
        bodyStyles: { fontSize: 7 },
        columnStyles: {
          0: { cellWidth: 30 },
          1: { cellWidth: 25 },
          2: { cellWidth: 25 },
          3: { cellWidth: 40 },
          4: { cellWidth: 30 },
          5: { cellWidth: 30 },
          6: { cellWidth: 'auto' },
        },
        margin: { top: 15, bottom: 15 },
        didDrawPage: () => addFooter(),
      });
    });

    addFooter();

    const periodSuffix = exportTglAwal || exportTglAkhir
      ? `_${exportTglAwal || 'all'}_${exportTglAkhir || 'all'}`
      : '';
    doc.save(`Laporan_CM_Per_Teknisi${periodSuffix}_${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header-row">
          <div>
            <h2>Corrective Maintenance (CM)</h2>
            <p className="page-desc">{data.length} laporan perbaikan</p>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button className="btn btn-secondary" onClick={exportCSV}
              style={{ whiteSpace: 'nowrap' }}>
              <Download size={15} /><span>Excel</span>
            </button>
            <button className="btn btn-secondary" onClick={exportPDF}
              style={{ whiteSpace: 'nowrap' }}>
              <FileText size={15} /><span>PDF</span>
            </button>
            <div style={{ position: 'relative' }}>
              <button
                className="btn btn-secondary"
                onClick={() => setShowExportFilter(!showExportFilter)}
                style={{
                  whiteSpace: 'nowrap',
                  border: showExportFilter || exportTglAwal || exportTglAkhir ? '1px solid #fb6340' : undefined,
                  background: showExportFilter || exportTglAwal || exportTglAkhir ? '#fff5ee' : undefined,
                }}
              >
                <Calendar size={15} />
                <span>Filter</span>
                {(exportTglAwal || exportTglAkhir) && (
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 18, height: 18, borderRadius: '50%',
                    background: '#2dce89', color: '#fff', fontSize: 10, fontWeight: 700,
                    marginLeft: 4,
                  }}>1</span>
                )}
              </button>
              {showExportFilter && (
                <div style={{
                  position: 'absolute', right: 0, top: '100%', marginTop: 4,
                  background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 8,
                  boxShadow: '0 4px 16px rgba(0,0,0,0.12)', padding: '12px 16px',
                  zIndex: 100, minWidth: 220,
                }}>
                  <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--gray-600)', marginBottom: 8 }}>
                    Filter Periode
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <label style={{ fontSize: '0.72rem', color: 'var(--gray-500)' }}>Dari</label>
                    <input type="date" value={exportTglAwal} onChange={e => setExportTglAwal(e.target.value)}
                      style={{ padding: '5px 8px', border: '1px solid var(--gray-200)', borderRadius: 5, fontSize: '0.78rem' }} />
                    <label style={{ fontSize: '0.72rem', color: 'var(--gray-500)' }}>Sampai</label>
                    <input type="date" value={exportTglAkhir} onChange={e => setExportTglAkhir(e.target.value)}
                      style={{ padding: '5px 8px', border: '1px solid var(--gray-200)', borderRadius: 5, fontSize: '0.78rem' }} />
                  </div>
                  <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                    <button className="btn btn-secondary" onClick={() => { setExportTglAwal(''); setExportTglAkhir(''); }}
                      style={{ fontSize: '0.72rem', padding: '4px 10px', flex: 1 }}>
                      Reset
                    </button>
                    <button className="btn btn-primary" onClick={() => setShowExportFilter(false)}
                      style={{ fontSize: '0.72rem', padding: '4px 10px', flex: 1, background: 'linear-gradient(135deg, #fb6340, #f5365c)', border: 'none' }}>
                      Tutup
                    </button>
                  </div>
                </div>
              )}
            </div>
            {activeTab === 'submission' && hasMenuAction('cm', 'create') && (
              <button className="btn btn-primary" onClick={() => { setShowForm(true); setEditingId(null); setEditingData(null); }}
                style={{ background: 'linear-gradient(135deg, #fb6340, #f5365c)', border: 'none' }}>
                {showForm ? <X size={16} /> : <Plus size={16} />}<span>{showForm ? 'Tutup Form' : 'Buat Pengajuan Baru'}</span>
              </button>
            )}
            
          </div>
        </div>
      </div>

      {/* ====== TAB NAVIGATION ====== */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 20 }}>
        {TABS.map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.key;
          const count = tabCounts[tab.key];
          const bgColor = tab.color;
          return (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
        padding: '10px 18px', borderRadius: 10, border: 'none',
        fontSize: '0.85rem', fontWeight: isActive ? 700 : 500,
        cursor: 'pointer', transition: 'all 0.2s',
        background: isActive ? bgColor : '#fff',
        color: isActive ? '#fff' : '#6b7280',
        boxShadow: isActive ? `0 4px 12px ${bgColor}40` : '0 1px 3px rgba(0,0,0,0.06)',
        flex: 1,
      }}>
      <Icon size={18} />
      <span>{tab.label}</span>
      <span style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        minWidth: 22, height: 22, borderRadius: 11,
        fontSize: '0.72rem', fontWeight: 700,
        background: isActive ? 'rgba(255,255,255,0.25)' : '#f3f4f6',
        color: isActive ? '#fff' : '#6b7280',
      }}>{count}</span>
            </button>
          );
        })}
      </div>

      {/* ====== GLOBAL FILTER BAR ====== */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'nowrap',
        marginBottom: 12, padding: '8px 12px', borderRadius: 10,
        background: '#fff', border: '1px solid var(--gray-100)', overflow: 'auto'
      }}>
        <div style={{ display: 'flex', flexWrap: 'nowrap', gap: 6, alignItems: 'center' }}>
          <div className="search-box" style={{ minWidth: 90, flex: '0 1 auto', maxWidth: 110 }}>
            <Search size={14} />
            <input type="text" value={filterNoBody} onChange={e => setFilterNoBody(e.target.value)} placeholder="No Body" />
          </div>
          <div className="search-box" style={{ minWidth: 90, flex: '0 1 auto', maxWidth: 110 }}>
            <Search size={14} />
            <input type="text" value={filterNoPolisi} onChange={e => setFilterNoPolisi(e.target.value)} placeholder="No Polisi" />
          </div>
          <div className="search-box" style={{ minWidth: 95, flex: '0 1 auto', maxWidth: 115 }}>
            <Search size={14} />
            <input type="text" value={filterTeknisi} onChange={e => setFilterTeknisi(e.target.value)} placeholder="Teknisi" />
          </div>
          <div className="search-box" style={{ minWidth: 95, flex: '0 1 auto', maxWidth: 115 }}>
            <MapPin size={14} style={{ color: 'var(--gray-400)' }} />
            <input type="text" value={filterLokasi} onChange={e => setFilterLokasi(e.target.value)} placeholder="Lokasi" />
          </div>
          {/* Nama Klien — multi-pilih (filter dijalankan di server) */}
          <MultiSelectFilter
            options={klienFilterOptions}
            selected={filterNamaKlien}
            onChange={v => { setFilterNamaKlien(v); setPage(1); }}
            placeholder="Nama Klien"
            searchPlaceholder="Cari klien..."
          />
          {/* No Kontrak — multi-pilih (diambil dari master armada) */}
          <MultiSelectFilter
            options={kontrakFilterOptions}
            selected={filterNoKontrak}
            onChange={v => { setFilterNoKontrak(v); setPage(1); }}
            placeholder="No Kontrak"
            searchPlaceholder="Cari no kontrak..."
          />
          <select value={filterJenisKendaraan} onChange={e => setFilterJenisKendaraan(e.target.value)}
            style={{ padding: '4px 6px', border: '1px solid var(--gray-200)', borderRadius: 6, fontSize: '0.72rem', background: '#fff', color: 'var(--gray-600)', cursor: 'pointer', maxWidth: 95 }}>
            <option value="">Semua</option>
            <option value="AC">❄ AC</option>
            <option value="Non AC">🔧 Non AC</option>
          </select>
          <div style={{ display: 'flex', alignItems: 'center', gap: 3, flexShrink: 0 }}>
            <Calendar size={12} className="text-muted" />
            <input type="date" value={tanggalAwal} onChange={e => setTanggalAwal(e.target.value)}
              style={{ padding: '4px 6px', border: '1px solid var(--gray-200)', borderRadius: 6, fontSize: '0.7rem', maxWidth: 95, background: '#fff' }}
              placeholder="Tgl. awal" />
            <span style={{ color: 'var(--gray-400)', fontSize: '0.7rem' }}>s/d</span>
            <input type="date" value={tanggalAkhir} onChange={e => setTanggalAkhir(e.target.value)}
              style={{ padding: '4px 6px', border: '1px solid var(--gray-200)', borderRadius: 6, fontSize: '0.7rem', maxWidth: 95, background: '#fff' }}
              placeholder="Tgl. akhir" />
          </div>
          {(filterNoBody || filterNoPolisi || filterTeknisi || filterLokasi || filterNamaKlien.length > 0 || filterNoKontrak.length > 0 || filterJenisKendaraan || globalSearch || globalStatusFilter || tanggalAwal || tanggalAkhir) && (
            <button className="btn-icon" onClick={() => { setFilterNoBody(''); setFilterNoPolisi(''); setFilterTeknisi(''); setFilterLokasi(''); setFilterNamaKlien([]); setFilterNoKontrak([]); setFilterJenisKendaraan(''); setGlobalSearch(''); setGlobalStatusFilter(''); setTanggalAwal(''); setTanggalAkhir(''); setPage(1); }} style={{ color: '#f5365c', flexShrink: 0 }} title="Reset filter">
              <X size={14} />
            </button>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
          <div className="search-box" style={{ minWidth: 160, maxWidth: 240 }}>
            <Search size={16} />
            <input type="text" value={searchInput} onChange={handleSearchChange} placeholder="Cari CM ID / No Polisi / Teknisi / Klien..." />
          </div>
          {activeTab === 'approved' && (
            <select value={globalStatusFilter} onChange={e => setGlobalStatusFilter(e.target.value)}
              style={{ padding: '6px 8px', border: '1px solid var(--gray-200)', borderRadius: 6, fontSize: '0.78rem', background: '#fff', minWidth: 120 }}>
              <option value="">Semua Status</option>
      {STATUS_OPTIONS.map(s => (
        <option key={s} value={s}>{s}</option>
      ))}
            </select>
          )}
        </div>
        <span style={{ fontSize: '0.78rem', color: 'var(--gray-400)', marginLeft: 'auto', whiteSpace: 'nowrap' }}>
          {total > 0 && `${total} total`}
          {activeTab === 'remainder' && `${remainderData.length} perlu perawatan`}
        </span>
      </div>

      {/* ====== TAB CONTENT ====== */}
      {loading ? (
        <div className="page-loading" style={{ padding: '20px 0' }}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 20 }}>
            {[1,2,3,4,5].map(i => (
              <div key={i} style={{
                flex: 1, height: 44, borderRadius: 10,
                background: 'linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%)',
                backgroundSize: '200% 100%',
                animation: 'skeletonPulse 1.5s ease infinite',
              }} />
            ))}
          </div>
          <div style={{
            height: 44, borderRadius: 10, marginBottom: 16,
            background: 'linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%)',
            backgroundSize: '200% 100%',
            animation: 'skeletonPulse 1.5s ease infinite',
          }} />
          <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
            {[1,2,3].map(i => (
              <div key={i} style={{
                flex: 1, height: 80, borderRadius: 10,
                background: 'linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%)',
                backgroundSize: '200% 100%',
                animation: 'skeletonPulse 1.5s ease infinite',
                animationDelay: `${i * 0.2}s`,
              }} />
            ))}
          </div>
          <div style={{
            height: 40, borderRadius: '8px 8px 0 0', marginBottom: 2,
            background: 'linear-gradient(90deg, #e8e8e8 25%, #ddd 50%, #e8e8e8 75%)',
            backgroundSize: '200% 100%',
            animation: 'skeletonPulse 1.5s ease infinite',
          }} />
          {[1,2,3,4,5].map(i => (
            <div key={i} style={{
              display: 'flex', gap: 8, padding: '10px 12px',
              borderBottom: '1px solid #f0f0f0',
              opacity: 1 - (i * 0.12),
            }}>
              <div style={{
                width: '15%', height: 16, borderRadius: 4,
                background: 'linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%)',
                backgroundSize: '200% 100%',
                animation: 'skeletonPulse 1.5s ease infinite',
                animationDelay: `${i * 0.1}s`,
              }} />
              <div style={{
                width: '25%', height: 16, borderRadius: 4,
                background: 'linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%)',
                backgroundSize: '200% 100%',
                animation: 'skeletonPulse 1.5s ease infinite',
                animationDelay: `${i * 0.1 + 0.1}s`,
              }} />
              <div style={{
                width: '20%', height: 16, borderRadius: 4,
                background: 'linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%)',
                backgroundSize: '200% 100%',
                animation: 'skeletonPulse 1.5s ease infinite',
                animationDelay: `${i * 0.1 + 0.2}s`,
              }} />
              <div style={{
                width: '25%', height: 16, borderRadius: 4,
                background: 'linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%)',
                backgroundSize: '200% 100%',
                animation: 'skeletonPulse 1.5s ease infinite',
                animationDelay: `${i * 0.1 + 0.3}s`,
              }} />
              <div style={{
                width: '15%', height: 16, borderRadius: 4,
                background: 'linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%)',
                backgroundSize: '200% 100%',
                animation: 'skeletonPulse 1.5s ease infinite',
                animationDelay: `${i * 0.1 + 0.4}s`,
              }} />
            </div>
          ))}
          <style>{`@keyframes skeletonPulse {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}`}</style>
        </div>
      ) : (
        <>
          {activeTab === 'submission' && (
            <>
              {(showForm || editingId) ? (
                /* ====== FORM VIEW ====== */
                <>
                  <div style={{ marginBottom: 12 }}>
                    <button className="btn btn-secondary" onClick={() => { setShowForm(false); setEditingId(null); setEditingData(null); }}
                      style={{ padding: '6px 14px', fontSize: '0.8rem', borderRadius: 6 }}>
                      ← Kembali ke Daftar
                    </button>
                  </div>
                  <FormPengajuan
                    onSaved={() => { setShowForm(false); setRefreshKey(k => k + 1); setPmInitData(null); }}
                    vehicles={vehicles}
                    spareparts={spareparts}
                    jasaLayanan={jasaLayanan}
                    karyawanList={karyawanList}
                    editingId={editingId}
                    editingData={editingData}
                    onCancelEdit={() => { setEditingId(null); setEditingData(null); setShowForm(false); setPmInitData(null); }}
                    pmInitData={pmInitData}
                  />
                </>
              ) : (
                /* ====== LIST VIEW ====== */
                <>
                  {submissionFiltered.filter(d => d.status === 'Pengajuan' || d.status === 'Menunggu Approval').length === 0 ? (
                    <div style={{
                      textAlign: 'center', padding: '60px 20px',
                      background: '#fff', borderRadius: 12, border: '2px dashed var(--gray-200)',
                    }}>
                      <FileText size={48} style={{ color: 'var(--gray-200)', marginBottom: 12 }} />
                      <h4 style={{ color: 'var(--gray-400)', margin: '0 0 8px', fontSize: '1rem' }}>Belum Ada Pengajuan CM</h4>
                      <p style={{ color: 'var(--gray-400)', fontSize: '0.82rem', margin: '0 0 16px' }}>
                        Klik tombol "Buat Pengajuan Baru" untuk membuat pengajuan corrective maintenance
                      </p>
                      {hasMenuAction('cm', 'create') && (
                        <button className="btn btn-primary" onClick={() => setShowForm(true)}
                          style={{ background: 'linear-gradient(135deg, #fb6340, #f5365c)', border: 'none' }}>
                          <Plus size={16} /><span>Buat Pengajuan Baru</span>
                        </button>
                      )}
                    </div>
                  ) : (
                    <>
                      <div className="table-wrapper" style={{ marginBottom: 0 }}>
                        <table className="data-table">
                          <thead>
                            <tr>
                              <th>No. CM</th><th>No. Body</th><th>No. Polisi</th><th>Rute</th><th>Nama Klien</th><th>Teknisi</th><th>Detail Pekerjaan</th><th>Status</th><th>Disetujui Oleh</th><th>Keterangan</th><th style={{ textAlign: 'right' }}>Total</th><th>Tanggal</th><th style={{ width: 140, textAlign: 'center' }}>Aksi</th>
                            </tr>
                          </thead>
                          <tbody>
                            {submissionFiltered.filter(d => d.status === 'Pengajuan' || d.status === 'Menunggu Approval').map(d => {
                              const gTotal = (d.sparepart_digunakan || []).reduce((s, sp) => s + (sp.jumlah || 0) * (sp.harga || 0), 0) +
                                (d.jasa_digunakan || []).reduce((s, js) => s + (js.jumlah || 0) * (js.harga || 0), 0);
                              const displayTotal = d.nominal_tagihan || gTotal;
                              return (              <tr key={d.id}>
                <td><code style={{ fontSize: '0.72rem', fontWeight: 600 }}>{d.cm_id || `#${d.id}`}</code></td>
                <td style={{ fontSize: '0.78rem' }}>{d.no_body || '-'}</td>
                <td style={{ fontSize: '0.78rem' }}>{d.no_polisi || '-'}</td>
                <td style={{ fontSize: '0.78rem', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={d.rute || ''}>{d.rute || '-'}</td>
                <td style={{ fontSize: '0.78rem', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.nama_klien || '-'}</td>
                <td style={{ fontSize: '0.78rem', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.nama_teknisi || d.teknisi || '-'}</td>
                <td style={{ fontSize: '0.75rem', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.detail_pekerjaan || '-'}</td>
                <td><StatusBadge status={d.status} size="sm" /></td>
                <td style={{ fontSize: '0.75rem', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.disetujui_oleh || '-'}</td>
                <td style={{ fontSize: '0.75rem', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.catatan || '-'}</td>
                <td style={{ fontSize: '0.78rem', fontWeight: 600, color: '#1aae6f', textAlign: 'right', whiteSpace: 'nowrap' }}>Rp {(displayTotal || 0).toLocaleString('id-ID')}</td>
                <td style={{ whiteSpace: 'nowrap', fontSize: '0.78rem' }}>{d.created_at ? new Date(d.created_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'short' }) : '-'}</td>
                <td>
                                  <div className="action-btns" style={{ justifyContent: 'center', gap: 4 }}>
                                    <button className="btn-icon" title="Lihat Detail" onClick={() => handleViewDetail(d)} style={{ color: '#5e72e4' }}>
                                      <Eye size={12} />
                                    </button>
                                    {d.status === 'Pengajuan' && (
                                      <>
                                        <button className="btn-icon" title="Edit" onClick={() => {
                                          const cmData = data.find(x => x.id === d.id);
                                          if (cmData) { setEditingId(d.id); setEditingData(cmData); setShowForm(true); }
                                        }} style={{ color: '#2dce89' }}><Edit2 size={12} /></button>
                                        <button className="btn-icon btn-danger" title="Hapus" onClick={async () => { if (window.confirm('Hapus pengajuan ini?')) { try { await deleteCM(d.id); setRefreshKey(k => k + 1); } catch(e) { alert('Gagal'); }}}}><Trash2 size={12} /></button>
                                      </>
                                    )}
                                    {d.status === 'Menunggu Approval' && (
                                      <>
                                        <button className="btn btn-sm" title="Setujui" onClick={() => {
                                          setApproveModal(d);
                                          setApproveRole('Pemilik');
                                          setApproveKeterangan('');
                                        }} style={{ background: '#2dce89', color: '#fff', border: 'none', padding: '3px 8px', borderRadius: 4, fontSize: '0.68rem', fontWeight: 600, cursor: 'pointer' }}>
                                          ✓ Setujui
                                        </button>
                                        <button className="btn btn-sm" title="Tolak" onClick={() => {
                                          setRejectModal(d);
                                          setRejectRole('Pemilik');
                                          setRejectKeterangan('');
                                        }} style={{ background: '#f5365c', color: '#fff', border: 'none', padding: '3px 8px', borderRadius: 4, fontSize: '0.68rem', fontWeight: 600, cursor: 'pointer' }}>
                                          ✕ Tolak
                                        </button>
                                      </>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                          </tbody>
                        </table>
                      </div>
                      <Pagination page={page} totalPages={totalPages} total={total} onPageChange={setPage} />
                    </>
                  )}
                </>
              )}
            </>
          )}

          {activeTab === 'approved' && (
            <>
              <TabStatus data={approvedFiltered} onRefresh={load} onViewDetail={(item) => handleViewDetail(item)} />
              <Pagination page={page} totalPages={totalPages} total={total} onPageChange={setPage} />
            </>
          )}

          {activeTab === 'rejected' && (
            <>
              <div className="table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>No. CM</th><th>No. Body</th><th>No. Polisi</th><th>Rute</th><th>Nama Klien</th><th>Teknisi</th><th>Detail Pekerjaan</th><th>Status</th><th>Tanggal</th><th style={{ width: 140, textAlign: 'center' }}>Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rejectedFiltered.map(d => (
                      <tr key={d.id}>
                        <td><code>{d.cm_id || `#${d.id}`}</code></td>
                        <td style={{ fontSize: '0.78rem' }}>{d.no_body || '-'}</td>
                        <td><strong>{d.no_polisi}</strong></td>
                        <td style={{ fontSize: '0.78rem', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={d.rute || ''}>{d.rute || '-'}</td>
                        <td>{d.nama_klien}</td>
                        <td>{d.nama_teknisi}</td>
                        <td style={{ fontSize: '0.78rem', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.detail_pekerjaan || '-'}</td>
                        <td>
                          <StatusBadge status={d.status} />
                        </td>
                        <td style={{ fontSize: '0.78rem' }}>{toWIBDate(d.created_at)}</td>
                      <td style={{ textAlign: 'center' }}>
                        <div className="action-btns" style={{ justifyContent: 'center', gap: 6 }}>                          <button className="btn-icon" title="Lihat Detail" onClick={() => { const cmData = data.find(x => x.id === d.id); if (cmData) handleViewDetail(cmData); }} style={{ color: '#11cdef' }}>
                              <Eye size={13} />
                            </button>
                          <button title="Ajukan Ulang" onClick={async () => {
                            if (!window.confirm('Yakin ingin mengajukan ulang CM ini ke Pengajuan?')) return;
                            try {
                              await approveCM(d.id, { action: 'resubmit' });
                              setRefreshKey(k => k + 1);
                            } catch(err) {
                              alert('Gagal mengajukan ulang: ' + (err.response?.data?.error || err.message));
                            }
                          }} style={{ color: '#2dce89', background: '#e8f8f0', padding: '4px 10px', borderRadius: 6, fontWeight: 600, fontSize: '0.72rem', display: 'inline-flex', alignItems: 'center', gap: 4, border: '1px solid #b8e6d0', cursor: 'pointer' }}>
                            <RotateCcw size={13} />
                            <span>Ajukan Ulang</span>
                          </button>
                        </div>
                      </td>
                      </tr>
                    ))}
                    {rejectedFiltered.length === 0 && <tr><td colSpan={9} style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--gray-400)' }}>Belum ada CM yang ditolak</td></tr>}
                  </tbody>
                </table>
              </div>
              <Pagination page={page} totalPages={totalPages} total={total} onPageChange={setPage} />
            </>
          )}

          {activeTab === 'completed' && (
            <>
              {(showForm || editingId) ? (
                /* ====== FORM EDIT CM SELESAI ====== */
                <>
                  <div style={{ marginBottom: 12 }}>
                    <button className="btn btn-secondary" onClick={() => { setShowForm(false); setEditingId(null); setEditingData(null); }}
                      style={{ padding: '6px 14px', fontSize: '0.8rem', borderRadius: 6 }}>
                      ← Kembali ke Daftar
                    </button>
                  </div>
                  <FormPengajuan
                    onSaved={() => { setShowForm(false); setRefreshKey(k => k + 1); setPmInitData(null); }}
                    vehicles={vehicles}
                    spareparts={spareparts}
                    jasaLayanan={jasaLayanan}
                    karyawanList={karyawanList}
                    editingId={editingId}
                    editingData={editingData}
                    onCancelEdit={() => { setEditingId(null); setEditingData(null); setShowForm(false); setPmInitData(null); }}
                    pmInitData={pmInitData}
                  />
                </>
              ) : (
                <>
              <div className="table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>No. CM</th><th>No. Body</th><th>No. Polisi</th><th>Rute</th><th>Nama Klien</th><th>Teknisi</th><th>Detail Pekerjaan</th><th>Status</th><th style={{ textAlign: 'right' }}>Total</th><th>Tanggal</th><th style={{ width: 200, textAlign: 'center' }}>Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {completedFiltered.map(d => {
                      const gTotal = (d.sparepart_digunakan || []).reduce((s, sp) => s + (sp.jumlah || 0) * (sp.harga || 0), 0) +
                        (d.jasa_digunakan || []).reduce((s, js) => s + (js.jumlah || 0) * (js.harga || 0), 0);
                      const displayTotal = d.nominal_tagihan || gTotal;
                      const tagihanItem = tagihanCMList.find(t => String(t.cm_id) === String(d.id));
                      return (
                      <tr key={d.id}>
                        <td><code style={{ fontSize: '0.72rem', fontWeight: 600 }}>{d.cm_id || `#${d.id}`}</code></td>
                        <td style={{ fontSize: '0.78rem' }}>{d.no_body || '-'}</td>
                        <td><strong style={{ fontSize: '0.82rem' }}>{d.no_polisi || '-'}</strong></td>
                        <td style={{ fontSize: '0.78rem', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={d.rute || ''}>{d.rute || '-'}</td>
                        <td style={{ fontSize: '0.78rem', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.nama_klien || '-'}</td>
                        <td style={{ fontSize: '0.78rem' }}>{d.nama_teknisi || '-'}</td>
                        <td style={{ fontSize: '0.75rem', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.detail_pekerjaan || '-'}</td>
                        <td><StatusBadge status={d.status} /></td>
                        <td style={{ fontSize: '0.78rem', fontWeight: 600, color: '#1aae6f', textAlign: 'right', whiteSpace: 'nowrap' }}>Rp {(displayTotal || 0).toLocaleString('id-ID')}</td>
                        <td style={{ fontSize: '0.78rem' }}>{toWIBDate(d.created_at)}</td>
                        <td style={{ textAlign: 'center' }}>
                          <div className="action-btns" style={{ justifyContent: 'center', gap: 4 }}>
                            <button className="btn-icon" title="Lihat Detail" onClick={() => handleViewDetail(d)} style={{ color: '#11cdef' }}>
                              <Eye size={14} />
                            </button>
                            <button className="btn-icon" title="Cetak" onClick={() => { handleViewDetail(d); }} style={{ color: '#5e72e4' }}>
                              <Printer size={14} />
                            </button>
                            {tagihanItem ? (
                              <button className="btn-icon" title="Tidak bisa diedit — hapus data tagihan terlebih dahulu" disabled style={{ color: '#c0c4cc', cursor: 'not-allowed', opacity: 0.55 }}>
                                <Edit2 size={14} />
                            </button>
                            ) : (
                              <button className="btn-icon" title="Edit CM Selesai" onClick={() => handleEditCompletedCM(d)} disabled={editLoadingId === d.id} style={{ color: editLoadingId === d.id ? '#c0c4cc' : '#2dce89' }}>
                                <Edit2 size={14} />
                              </button>
                            )}
                            {tagihanItem ? (
                                <span className="badge" title={tagihanItem.invoice_no ? `Sudah dibuat tagihan ${tagihanItem.invoice_no}` : 'Sudah dibuat tagihan'}
                                  style={{ background: '#d1fae5', color: '#065f46', fontSize: '0.65rem', padding: '3px 8px', borderRadius: 12, whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                  <CheckCircle size={12} /> Sudah Dibuat Tagihan
                                </span>
                              ) : (
                              <button className="btn-icon" title="Buat Tagihan" onClick={async () => { try { const res = await generateTagihanFromCM({ cm_id: d.id }); sessionStorage.setItem('tagihan_notif','✅ Tagihan '+ (res.data?.invoice_no || '') +' berhasil dibuat!'); window.location.href = '/tagihan'; } catch(e) { if (e.response?.status === 409) { sessionStorage.setItem('tagihan_notif','⚠️ CM '+ (d.cm_id || '#') +' sudah memiliki tagihan'); window.location.href = '/tagihan'; } else { alert('Gagal: ' + (e.response?.data?.error || e.message)); } }}} style={{ color: '#2dce89' }}>
                                <span style={{ fontWeight: 700, fontSize: 12 }}>Rp</span>
                              </button>
                              )}
                            {tagihanItem ? (
                              <button className="btn-icon btn-danger" title="Tidak bisa dihapus — hapus data tagihan terlebih dahulu" disabled style={{ color: '#c0c4cc', cursor: 'not-allowed', opacity: 0.55 }}>
                                <Trash2 size={14} />
                              </button>
                            ) : (
                              <button className="btn-icon btn-danger" title="Hapus" onClick={async () => { if (window.confirm('Hapus CM ini?')) { try { await deleteCM(d.id); setRefreshKey(k => k + 1); } catch(e) { alert('Gagal'); }}}} style={{ color: '#f5365c' }}>
                                <Trash2 size={14} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );})}
                    {completedFiltered.length === 0 && <tr><td colSpan={10} style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--gray-400)' }}>Belum ada CM yang selesai</td></tr>}
                  </tbody>
                </table>
              </div>
              <Pagination page={page} totalPages={totalPages} total={total} onPageChange={setPage} />
                </>
              )}
            </>
          )}

          {activeTab === 'remainder' && (
            <div className="table-wrapper">
              <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: '#fffbeb', borderRadius: 8, border: '1px solid #fde68a', fontSize: '0.82rem', color: '#92400e' }}>
                <Clock size={16} />
                <span>Reminder perawatan AC rutin — kendaraan yang pernah mengganti <strong>Filter Dryer</strong> dan sudah mendekati 6 bulan sejak penggantian terakhir.</span>
              </div>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>No. CM</th><th>No. Body</th><th>No. Polisi</th><th>Rute</th><th>Nama Klien</th><th>Filter Dryer</th><th>Tgl. Pasang</th><th>Jatuh Tempo</th><th>Sisa Hari</th><th>Status</th><th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {remainderData.length === 0 ? (
                    <tr><td colSpan={11} style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--gray-400)' }}>Tidak ada data remainder untuk kendaraan AC dengan Filter Dryer</td></tr>
                  ) : (
                    remainderData.map((d, idx) => (
                      <tr key={d.id || idx}>
                        <td><code style={{ fontSize: '0.72rem', fontWeight: 600 }}>{d.cm_id || `#${d.id}`}</code></td>
                        <td style={{ fontSize: '0.78rem' }}>{d.no_body || '-'}</td>
                        <td><strong style={{ fontSize: '0.82rem' }}>{d.no_polisi || '-'}</strong></td>
                        <td style={{ fontSize: '0.78rem', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={d.rute || ''}>{d.rute || '-'}</td>
                        <td style={{ fontSize: '0.78rem', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.nama_klien || '-'}</td>
                        <td style={{ fontSize: '0.78rem' }}>{d.filterDryerNama}</td>
                        <td style={{ fontSize: '0.78rem' }}>{new Date(d.replaceDate).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                        <td style={{ fontSize: '0.78rem', fontWeight: 600 }}>{new Date(d.dueDate).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                        <td style={{ fontSize: '0.78rem', textAlign: 'center', fontWeight: 600, color: d.daysUntilDue <= 0 ? '#f5365c' : d.daysUntilDue <= 14 ? '#fb6340' : '#2dce89' }}>{d.daysUntilDue <= 0 ? '0' : d.daysUntilDue}</td>
                        <td>
                          <span className="badge" style={{ background: d.statusColor + '20', color: d.statusColor, padding: '3px 10px', borderRadius: 12, fontSize: '0.72rem', fontWeight: 600 }}>
                            {d.reminderStatus}
                          </span>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <button className="btn-icon" title="Lihat Detail CM" onClick={() => handleViewDetail(d)} style={{ color: '#11cdef' }}>
                            <Eye size={13} />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

        </>
      )}

      {/* Detail Modal */}
      {detailItem && (
        <DetailModal
          item={detailItem}
          onClose={() => setDetailItem(null)}
          onVerifikasi={load}
          jasaLayanan={jasaLayanan}
          spareparts={spareparts}
        />
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* ===== Approval Validation Modal ===== */}
      {approveModal && (
        <div className="modal-overlay" onClick={() => setApproveModal(null)}
          style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="modal-content" onClick={e => e.stopPropagation()}
            style={{ maxWidth: 450, width: '90%', borderRadius: 12, padding: 0, overflow: 'hidden' }}>
            <div className="modal-header"
              style={{ background: 'linear-gradient(135deg, #2dce89, #1aae6f)', color: '#fff', padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h4 style={{ margin: 0, color: '#fff', fontSize: '0.95rem' }}>Validasi Persetujuan CM</h4>
                <small style={{ opacity: 0.85 }}>{approveModal.cm_id || `#${approveModal.id}`} — {approveModal.no_polisi || ''}</small>
              </div>
              <button className="btn-icon" onClick={() => setApproveModal(null)} style={{ color: '#fff' }}><X size={18} /></button>
            </div>
            <div style={{ padding: '20px', background: '#fff' }}>
              <div className="form-group">
                <label style={{ fontWeight: 600, fontSize: '0.82rem', marginBottom: 6, display: 'block' }}>Disetujui Oleh</label>
                <select value={approveRole} onChange={e => setApproveRole(e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--gray-200)', borderRadius: 6, fontSize: '0.85rem', background: '#fff' }}>
                  <option value="Pemilik">Pemilik</option>
                  <option value="Satgas">Satgas</option>
                  <option value="Pramudi">Pramudi</option>
                </select>
              </div>
              <div className="form-group" style={{ marginTop: 14 }}>
                <label style={{ fontWeight: 600, fontSize: '0.82rem', marginBottom: 6, display: 'block' }}>Keterangan</label>
                <textarea value={approveKeterangan} onChange={e => setApproveKeterangan(e.target.value)}
                  placeholder="Masukkan keterangan persetujuan..."
                  rows={3}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--gray-200)', borderRadius: 6, fontSize: '0.85rem', resize: 'vertical', fontFamily: 'inherit' }}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
                <button className="btn btn-secondary" onClick={() => setApproveModal(null)}>
                  Batal
                </button>
                <button className="btn btn-primary" onClick={async () => {
                  try {
                    await approveCM(approveModal.id, { action: 'approve', catatan: approveKeterangan, disetujui_oleh: approveRole });
                    setApproveModal(null);
                    setRefreshKey(k => k + 1);
                  } catch(e) {
                    alert('Gagal approve: ' + (e.response?.data?.error || e.message));
                  }
                }} style={{ background: 'linear-gradient(135deg, #2dce89, #1aae6f)', border: 'none' }}>
                  <Save size={14} style={{ marginRight: 4 }} /> Simpan
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* ===== Reject Validation Modal ===== */}
      {rejectModal && (
        <div className="modal-overlay" onClick={() => setRejectModal(null)}
          style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="modal-content" onClick={e => e.stopPropagation()}
            style={{ maxWidth: 450, width: '90%', borderRadius: 12, padding: 0, overflow: 'hidden' }}>
            <div className="modal-header"
              style={{ background: 'linear-gradient(135deg, #f5365c, #dc2626)', color: '#fff', padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h4 style={{ margin: 0, color: '#fff', fontSize: '0.95rem' }}>Tolak CM</h4>
                <small style={{ opacity: 0.85 }}>{rejectModal.cm_id || `#${rejectModal.id}`} — {rejectModal.no_polisi || ''}</small>
              </div>
              <button className="btn-icon" onClick={() => setRejectModal(null)} style={{ color: '#fff' }}><X size={18} /></button>
            </div>
            <div style={{ padding: '20px', background: '#fff' }}>
              <div className="form-group">
                <label style={{ fontWeight: 600, fontSize: '0.82rem', marginBottom: 6, display: 'block' }}>Ditolak Oleh</label>
                <select value={rejectRole} onChange={e => setRejectRole(e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--gray-200)', borderRadius: 6, fontSize: '0.85rem', background: '#fff' }}>
                  <option value="Pemilik">Pemilik</option>
                  <option value="Satgas">Satgas</option>
                  <option value="Pramudi">Pramudi</option>
                </select>
              </div>
              <div className="form-group" style={{ marginTop: 14 }}>
                <label style={{ fontWeight: 600, fontSize: '0.82rem', marginBottom: 6, display: 'block' }}>Alasan Penolakan</label>
                <textarea value={rejectKeterangan} onChange={e => setRejectKeterangan(e.target.value)}
                  placeholder="Masukkan alasan penolakan..."
                  rows={3}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--gray-200)', borderRadius: 6, fontSize: '0.85rem', resize: 'vertical', fontFamily: 'inherit' }}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
                <button className="btn btn-secondary" onClick={() => setRejectModal(null)}>
                  Batal
                </button>
                <button className="btn btn-primary" onClick={async () => {
                  try {
                    await approveCM(rejectModal.id, { action: 'reject', catatan: rejectKeterangan, disetujui_oleh: rejectRole });
                    setRejectModal(null);
                    setRefreshKey(k => k + 1);
                  } catch(e) {
                    alert('Gagal tolak: ' + (e.response?.data?.error || e.message));
                  }
                }} style={{ background: 'linear-gradient(135deg, #f5365c, #dc2626)', border: 'none' }}>
                  <X size={14} style={{ marginRight: 4 }} /> Tolak
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
